import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Clock, CircleDashed, Check } from 'lucide-react';
import './App.css';
import supabase from './supabase';
import LandingPage from './LandingPage';
import OnboardingModal from './OnboardingModal';

const logo = 'https://res.cloudinary.com/dqzdn543t/image/upload/v1778932146/feja-logo-light-removebg-preview_1_lzt9vu.png';

const BG_PLACEHOLDER = 'https://res.cloudinary.com/dqzdn543t/image/upload/w_30,q_1,e_blur:1000/v1778979663/lazyload-feja-background_u7uonw.png';
const BG_FULL = 'https://res.cloudinary.com/dqzdn543t/image/upload/v1778979663/lazyload-feja-background_u7uonw.png';
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

const MOCK_USER = { id: null, email: 'test@feja.com', user_metadata: { name: 'Jaye', role: 'admin' } };

function LinktreePage({ onOpenApp, onSignedUp, inviteVenueId, startOnForm }) {
  const [showForm, setShowForm] = useState(!!inviteVenueId || !!startOnForm);
  const [formName, setFormName] = useState('');
  const [formVenue, setFormVenue] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [legalDoc, setLegalDoc] = useState(null);
  const [showFormPassword, setShowFormPassword] = useState(false);

  const handleSignUp = async () => {
    if (!formName.trim() || (!inviteVenueId && !formVenue.trim()) || !formEmail.trim() || !formPassword.trim()) return;
    setFormLoading(true);
    setFormError('');
    const metadata = inviteVenueId
      ? { name: formName.trim(), invited_venue_id: inviteVenueId }
      : { name: formName.trim(), venue_name: formVenue.trim() };
    const { data, error } = await supabase.auth.signUp({ email: formEmail.trim(), password: formPassword.trim(), options: { data: metadata } });
    if (error) { setFormError(error.message); setFormLoading(false); return; }
    if (!data?.user) { setFormLoading(false); return; }
    const signedInUser = data.user;
    if (inviteVenueId) {
      // Always read the role from the DB — never trust the URL param, which
      // an invitee could edit to self-promote to admin.
      const { data: inviteData } = await supabase.from('invites').select('role').eq('venue_id', inviteVenueId).eq('email', formEmail.trim()).single();
      const inviteRole = inviteData?.role || 'staff';
      const { error: profileError } = await supabase.from('profiles').insert({ id: signedInUser.id, name: formName.trim(), venue_id: inviteVenueId, role: inviteRole });
      if (profileError) { setFormError('Could not create profile. Please try again.'); setFormLoading(false); return; }
      const { error: inviteError } = await supabase.from('invites').update({ accepted_at: new Date().toISOString() }).eq('venue_id', inviteVenueId).eq('email', formEmail.trim());
      if (inviteError) console.error('Invite accepted_at update failed:', inviteError);
    } else {
      const venueId = crypto.randomUUID();
      const { error: venueError } = await supabase.from('venues').insert({ id: venueId, name: formVenue.trim() });
      if (venueError) { setFormError('Could not create venue. Please try again.'); setFormLoading(false); return; }
      const { error: profileError } = await supabase.from('profiles').insert({ id: signedInUser.id, name: formName.trim(), venue_id: venueId, role: 'admin' });
      if (profileError) { setFormError('Could not create profile. Please try again.'); setFormLoading(false); return; }
    }
    setFormLoading(false);
    onSignedUp(signedInUser);
  };

  return (
    <div className="screen login-page">
      <div className="login-top">
        <div className="login-logo-sm"><img src={logo} alt="Feja" /></div>
        <p className="login-sub">{showForm ? (inviteVenueId ? "You've been invited to join a team" : 'Create your account') : 'Kitchen compliance, made simple.'}</p>
      </div>
      <div className="login-form">
        {showForm ? (
          <>
            {!inviteVenueId && <button className="login-back-btn" style={{alignSelf:'flex-start'}} onClick={() => onOpenApp()}>←</button>}
            {inviteVenueId && <p className="lp-invite-heading">Create your account below to get started.</p>}
            <label className="f-lbl">Your name</label>
            <input className="f-input" placeholder="Jane Smith" value={formName} onChange={e => setFormName(e.target.value)} />
            {!inviteVenueId && <>
              <label className="f-lbl">Venue name</label>
              <input className="f-input" placeholder="The Corner Cafe" value={formVenue} onChange={e => setFormVenue(e.target.value)} />
            </>}
            <label className="f-lbl">Email</label>
            <input className="f-input" type="email" placeholder="you@venue.com" value={formEmail} onChange={e => setFormEmail(e.target.value)} />
            <label className="f-lbl">Password</label>
            <div className="pw-wrap">
              <input className="f-input" type={showFormPassword ? 'text' : 'password'} placeholder="Choose a password" value={formPassword} onChange={e => setFormPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && agreed && handleSignUp()} />
              <button type="button" className="pw-toggle" onClick={() => setShowFormPassword(p => !p)}>
                {showFormPassword
                  ? <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
              </button>
            </div>
            <label className="legal-checkbox-row">
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
              <span>I agree to the <button type="button" className="legal-inline-link" onClick={() => setLegalDoc('terms')}>Terms &amp; Conditions</button> and <button type="button" className="legal-inline-link" onClick={() => setLegalDoc('privacy')}>Privacy Policy</button></span>
            </label>
            {formError && <p className="auth-error">{formError}</p>}
            <button className="btn-primary" onClick={handleSignUp} disabled={formLoading || !agreed}>{formLoading ? 'Setting up...' : 'Create account'}</button>
          </>
        ) : (
          <>
            <button className="btn-primary" onClick={onOpenApp}>Sign in</button>
            <p className="forgot" style={{textAlign:'center',marginTop:'12px'}} onClick={() => setShowForm(true)}>Don't have an account? Sign up</p>
          </>
        )}
      </div>
      {legalDoc && <LegalModal doc={legalDoc} onClose={() => setLegalDoc(null)} />}
    </div>
  );
}

const SECTION_TYPES = {
  cold:      { label: 'Fridge',           threshold: { max: 5 },   correctiveActions: ['Moved to working fridge', 'Supervisor notified', 'Repair booked', 'Stock discarded'] },
  freezer:   { label: 'Freezer',          threshold: { max: -18 }, correctiveActions: ['Moved to working freezer', 'Supervisor notified', 'Repair booked', 'Stock discarded'] },
  hot:       { label: 'Hot Holding',      threshold: { min: 60 },  correctiveActions: ['Reheated', 'Discarded', 'Supervisor notified'] },
  reheating: { label: 'Cooking',          threshold: { min: 75 },  correctiveActions: ['Reheated again', 'Discarded', 'Supervisor notified'] },
  delivery:  { label: 'Delivery',         threshold: { max: 5 },   correctiveActions: ['Delivery rejected', 'Supplier notified', 'Stock discarded'] },
  custom:    { label: 'Custom',           threshold: {},            correctiveActions: [] },
};

const LOG_SECTIONS = [
  { id: 'fridge-1',  title: 'Fridge 1',   type: 'cold',      threshold: { max: 5 },  correctiveActions: ['Moved to working fridge', 'Supervisor notified', 'Repair booked', 'Stock discarded'], items: ['Fridge 1'] },
  { id: 'fridge-2',  title: 'Fridge 2',   type: 'cold',      threshold: { max: 5 },  correctiveActions: ['Moved to working fridge', 'Supervisor notified', 'Repair booked', 'Stock discarded'], items: ['Fridge 2'] },
  { id: 'freezer-1', title: 'Freezer 1',  type: 'freezer',   threshold: { max: -18 }, correctiveActions: ['Moved to working freezer', 'Supervisor notified', 'Repair booked', 'Stock discarded'], items: ['Freezer 1'] },
  { id: 'hot-hold',  title: 'Hot Holding', type: 'hot',       threshold: { min: 60 }, correctiveActions: ['Reheated', 'Discarded', 'Supervisor notified'], items: ['Hot Holding'] },
];

function isGrouped(s) {
  return s != null && typeof s === 'object' && !Array.isArray(s) && s.grouped === true;
}
function hasSections(s) {
  if (!s) return false;
  if (isGrouped(s)) return (s.groups || []).some(g => (g.sections || []).length > 0);
  return Array.isArray(s) && s.length > 0;
}
function flattenSections(s) {
  if (!s) return [];
  if (isGrouped(s)) return (s.groups || []).flatMap(g => g.sections || []);
  return Array.isArray(s) ? s : [];
}
function getSectionGroup(grouped, sectionId) {
  if (!isGrouped(grouped)) return null;
  for (const g of (grouped.groups || [])) {
    if ((g.sections || []).some(s => s.id === sectionId)) return g;
  }
  return null;
}

function getResult(threshold, temp, buffer) {
  if (temp === '') return null;
  const t = parseFloat(temp);
  if (isNaN(t)) return null;
  if (threshold.max === undefined && threshold.min === undefined) return null;
  const buf = buffer ?? 0;
  if (threshold.max !== undefined) {
    if (t <= threshold.max) return 'pass';
    if (buf > 0 && t <= threshold.max + buf) return 'monitor';
    return 'fail';
  }
  if (threshold.min !== undefined) {
    if (t >= threshold.min) return 'pass';
    if (buf > 0 && t >= threshold.min - buf) return 'monitor';
    return 'fail';
  }
  return null;
}

function getEquipmentGroup(section) {
  if (section.threshold?.min !== undefined) {
    return section.threshold.min >= 75 ? 'cooking' : 'hot';
  }
  if (section.threshold?.max !== undefined) {
    return section.threshold.max <= 0 ? 'freezers' : 'fridges';
  }
  return 'other';
}
function getDefaultBuffer(section) {
  const group = getEquipmentGroup(section);
  return { fridges: 2, freezers: 3, hot: 5, cooking: 0, other: 0 }[group] ?? 0;
}

const GROUP_META = {
  fridges:  { label: 'Fridges',     order: 0 },
  freezers: { label: 'Freezers',    order: 1 },
  hot:      { label: 'Hot Holding', order: 2 },
  cooking:  { label: 'Cooking',     order: 3 },
  other:    { label: 'Other',       order: 4 },
};

let _installPrompt = null;

function urlBase64ToUint8Array(b64) {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + pad).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(base64), c => c.charCodeAt(0));
}

async function subscribeToPush(user, venue) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !VAPID_PUBLIC_KEY) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }
    if (user?.id) {
      await supabase.from('push_subscriptions').upsert(
        { user_id: user.id, venue_id: venue?.id || null, subscription: sub.toJSON() },
        { onConflict: 'user_id' }
      );
    }
    return true;
  } catch { return false; }
}

function sendPushToAdmins(venue, payload) {
  if (!venue?.id) return;
  supabase.functions.invoke('send-push-notification', {
    body: { venue_id: venue.id, ...payload, target: 'admins' },
  }).catch(() => {});
}

function NotificationsSheet({ user, venue, onDone }) {
  const [loading, setLoading] = useState(false);
  const permission = typeof Notification !== 'undefined' ? Notification.permission : 'default';

  const handleEnable = async () => {
    setLoading(true);
    const perm = await Notification.requestPermission();
    if (perm === 'granted') await subscribeToPush(user, venue);
    setLoading(false);
    localStorage.setItem('feja_notif_asked', '1');
    onDone();
  };

  const handleLater = () => { localStorage.setItem('feja_notif_asked', '1'); onDone(); };

  return (
    <div className="notif-sheet-overlay" onClick={handleLater}>
      <div className="notif-sheet" onClick={e => e.stopPropagation()}>
        {permission === 'granted' ? (
          <>
            <div className="notif-sheet-icon">✅</div>
            <div className="notif-sheet-title">Notifications are on</div>
            <p className="notif-sheet-body">You'll be alerted when temps fail or corrective actions are logged.</p>
            <button className="btn-primary notif-sheet-btn" onClick={onDone}>Got it</button>
          </>
        ) : permission === 'denied' ? (
          <>
            <div className="notif-sheet-icon">🔕</div>
            <div className="notif-sheet-title">Notifications blocked</div>
            <p className="notif-sheet-body">To receive alerts, enable notifications for this site in your browser settings.</p>
            <button className="btn-primary notif-sheet-btn" onClick={onDone}>OK</button>
          </>
        ) : (
          <>
            <div className="notif-sheet-icon">🔔</div>
            <div className="notif-sheet-title">Stay on top of compliance</div>
            <p className="notif-sheet-body">Get instant alerts when temps fail or corrective actions are logged, straight to your device.</p>
            <button className="btn-primary notif-sheet-btn" onClick={handleEnable} disabled={loading}>
              {loading ? 'Setting up…' : 'Turn on notifications'}
            </button>
            <button className="notif-sheet-later" onClick={handleLater}>Maybe later</button>
          </>
        )}
      </div>
    </div>
  );
}

function InstallSheet({ onDone }) {
  const isIOS = /ipad|iphone|ipod/i.test(navigator.userAgent) &&
    /safari/i.test(navigator.userAgent) && !/crios|fxios|chrome/i.test(navigator.userAgent);
  const canInstall = !!_installPrompt || isIOS;

  if (!canInstall) { onDone(); return null; }

  const handleInstall = async () => {
    if (_installPrompt) {
      _installPrompt.prompt();
      await _installPrompt.userChoice;
      _installPrompt = null;
    }
    localStorage.setItem('feja_install_dismissed', '1');
    onDone();
  };

  const handleLater = () => { localStorage.setItem('feja_install_dismissed', '1'); onDone(); };

  return (
    <div className="notif-sheet-overlay" onClick={handleLater}>
      <div className="notif-sheet" onClick={e => e.stopPropagation()}>
        <div className="notif-sheet-icon">📲</div>
        <div className="notif-sheet-title">Add Feja to your home screen</div>
        {isIOS ? (
          <p className="notif-sheet-body">
            Tap <strong>Share</strong> at the bottom of your browser, then <strong>Add to Home Screen</strong> — opens instantly like an app.
          </p>
        ) : (
          <p className="notif-sheet-body">
            Install Feja on your home screen for instant access — no app store needed.
          </p>
        )}
        {!isIOS && (
          <button className="btn-primary notif-sheet-btn" onClick={handleInstall}>
            Add to home screen
          </button>
        )}
        <button className="notif-sheet-later" onClick={handleLater}>
          {isIOS ? 'Got it' : 'Maybe later'}
        </button>
      </div>
    </div>
  );
}

function Toast({ msg, type }) {
  return <div className={`toast${type !== 'default' ? ` toast--${type}` : ''}`}>{msg}</div>;
}

function LazyBackground() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const img = new Image();
    img.onload = () => setLoaded(true);
    img.src = BG_FULL;
    if (img.complete) setLoaded(true);
  }, []);
  return (
    <div className="app-bg">
      <div className="app-bg-lo" style={{ backgroundImage: `url(${BG_PLACEHOLDER})` }} />
      <div className="app-bg-hi" style={{ backgroundImage: `url(${BG_FULL})`, opacity: loaded ? 1 : 0 }} />
    </div>
  );
}


function NotFound404() {
  return (
    <div className="nf-screen">
      <LazyBackground />
      <div className="nf-card">
        <img src={logo} alt="Feja" className="nf-logo" />
        <div className="nf-code">404</div>
        <div className="nf-title">Page not found</div>
        <p className="nf-sub">This page doesn't exist. Head back to the app.</p>
        <button className="btn-primary nf-btn" onClick={() => { window.location.href = '/'; }}>Go to Feja</button>
      </div>
    </div>
  );
}

function LegalModal({ doc, onClose }) {
  return (
    <div className="legal-overlay" onClick={onClose}>
      <div className="legal-modal" onClick={e => e.stopPropagation()}>
        <div className="legal-modal-top">
          <div>
            <span className="legal-doc-label">Legal</span>
            <h2 className="legal-modal-title">
              {doc === 'privacy' ? 'Privacy Policy' : doc === 'terms' ? 'Terms & Conditions' : 'Refund Policy'}
            </h2>
          </div>
          <button className="legal-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="legal-modal-body">
          {doc === 'privacy' && <PrivacyContent />}
          {doc === 'terms' && <TermsContent />}
          {doc === 'refund' && <RefundContent />}
        </div>
      </div>
    </div>
  );
}

function PrivacyContent() {
  return (
    <>
      <p className="legal-meta">Last updated: May 2026 · Feja by Jaye · Victoria, Australia</p>
      <p>Feja ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, store, and share information when you use Feja. We comply with the <strong>Privacy Act 1988 (Cth)</strong> and the Australian Privacy Principles (APPs).</p>
      <h3>1. Information We Collect</h3>
      <ul>
        <li><strong>Account information:</strong> Name, email address, business name, and role.</li>
        <li><strong>Venue data:</strong> Kitchen or venue name, address, and operating details.</li>
        <li><strong>Compliance records:</strong> Temperature logs, shift checklists, and food safety records.</li>
        <li><strong>Billing information:</strong> Processed securely through Stripe. We do not store card details.</li>
        <li><strong>Usage data:</strong> Log files, device type, browser, IP address, and feature usage.</li>
      </ul>
      <h3>2. How We Use Your Information</h3>
      <ul>
        <li>To provide and operate the Feja platform.</li>
        <li>To store and display your food safety compliance records.</li>
        <li>To process subscription payments via Stripe.</li>
        <li>To send transactional emails (account verification, receipts).</li>
        <li>To improve the platform through anonymised usage analytics.</li>
      </ul>
      <h3>3. Data Storage and Security</h3>
      <p>Your data is stored in Supabase on secure cloud infrastructure. We use Row-Level Security (RLS) to ensure each venue can only access its own data. Data is encrypted in transit (TLS) and at rest.</p>
      <h3>4. Data Sharing</h3>
      <p>We do not sell your personal information. We share data only with trusted providers needed to operate Feja: <strong>Stripe</strong> (payments), <strong>Supabase</strong> (database and auth), and email providers (transactional comms).</p>
      <h3>5. Data Retention</h3>
      <p>We retain your data while your subscription is active and for up to 7 years after account closure to comply with FSANZ Standard 3.2.2A Australian food safety record-keeping requirements.</p>
      <h3>6. Your Rights</h3>
      <p>Under the Privacy Act 1988, you have the right to access, correct, or request deletion of your personal information, and to opt out of marketing communications at any time.</p>
      <h3>7. Changes to This Policy</h3>
      <p>We may update this policy from time to time and will notify you of material changes via email or in-app notice.</p>
      <h3>8. Contact</h3>
      <div className="legal-contact-box">
        <p><strong>Email:</strong> hello@feja.com.au</p>
        <p><strong>Location:</strong> Victoria, Australia</p>
      </div>
    </>
  );
}

function TermsContent() {
  return (
    <>
      <p className="legal-meta">Last updated: May 2026 · Feja by Jaye · Victoria, Australia</p>
      <p>These Terms and Conditions govern your access to and use of the Feja platform. By creating an account or using Feja, you agree to be bound by these Terms.</p>
      <div className="legal-highlight-box">
        <p><strong>Important:</strong> Feja is a record-keeping tool. It does not guarantee compliance with any food safety legislation. It is your responsibility to ensure your business meets all applicable legal requirements.</p>
      </div>
      <h3>1. About Feja</h3>
      <p>Feja is a food safety and kitchen compliance platform for commercial kitchens. Operated by Jaye, Victoria, Australia.</p>
      <h3>2. Eligibility</h3>
      <p>You must be at least 18 years old and have authority to bind your business to these Terms. Feja is intended for commercial food businesses operating in Australia.</p>
      <h3>3. Accounts and Access</h3>
      <p>You are responsible for maintaining the confidentiality of your login credentials and all activity under your account, including ensuring your staff comply with these Terms.</p>
      <h3>4. Subscription and Billing</h3>
      <ul>
        <li>$30 AUD per venue per month (plus GST where applicable).</li>
        <li>14-day free trial for new venues, no credit card required to start.</li>
        <li>Billing begins at the end of the trial. Cancel before then to avoid charges.</li>
        <li>Payments processed via Stripe. Subscriptions renew automatically each month.</li>
        <li>Cancel any time from account settings, takes effect at end of the current billing period.</li>
        <li>We reserve the right to change pricing with 30 days' notice to existing subscribers.</li>
      </ul>
      <h3>5. Acceptable Use</h3>
      <p>You agree not to use Feja for any unlawful purpose, attempt to access other venues' data, reverse engineer any part of the service, misrepresent compliance records, or resell access to the platform.</p>
      <h3>6. Your Data</h3>
      <p>You own the compliance data you enter into Feja. We recommend regular exports for your own backup. Feja is not liable for data loss caused by factors outside our reasonable control.</p>
      <h3>7. Limitation of Liability</h3>
      <p>To the maximum extent permitted by Australian law, our total liability for any claim will not exceed the total amount you paid us in the 3 months preceding the claim.</p>
      <h3>8. Termination</h3>
      <p>We may suspend or terminate your account if you breach these Terms or fail to pay. Upon termination, data is retained for up to 7 years per FSANZ requirements then deleted.</p>
      <h3>9. Governing Law</h3>
      <p>These Terms are governed by the laws of Victoria, Australia.</p>
      <h3>10. Contact</h3>
      <div className="legal-contact-box">
        <p><strong>Email:</strong> hello@feja.com.au</p>
        <p><strong>Location:</strong> Victoria, Australia</p>
      </div>
    </>
  );
}

function RefundContent() {
  return (
    <>
      <p className="legal-meta">Last updated: May 2026 · Feja by Jaye · Victoria, Australia</p>
      <p>We want you to be satisfied with Feja. This policy explains when refunds are available and how to request one.</p>
      <div className="legal-summary-grid">
        <div className="legal-summary-card legal-card-green"><div className="legal-card-label">Free Trial</div><div className="legal-card-value">14 days, no charge</div></div>
        <div className="legal-summary-card legal-card-green"><div className="legal-card-label">Cancellation</div><div className="legal-card-value">Any time, no penalty</div></div>
        <div className="legal-summary-card legal-card-amber"><div className="legal-card-label">Refund Window</div><div className="legal-card-value">7 days from charge</div></div>
        <div className="legal-summary-card legal-card-amber"><div className="legal-card-label">Partial Refunds</div><div className="legal-card-value">Case by case</div></div>
      </div>
      <h3>1. Free Trial</h3>
      <p>All new venues receive a 14-day free trial. You will not be charged during this period. Cancel before it ends and no payment will be taken.</p>
      <h3>2. Cancellation</h3>
      <p>Cancel at any time from account settings. Takes effect at the end of your current billing period, no charge for the following period.</p>
      <h3>3. Refund Eligibility</h3>
      <ul>
        <li><strong>Within 7 days of your first paid charge:</strong> Contact us and we'll issue a full refund, no questions asked.</li>
        <li><strong>Billing errors:</strong> Incorrect charges (e.g. charged twice, charged after cancellation) will be refunded in full.</li>
        <li><strong>Extended outages:</strong> Verified outages over 48 consecutive hours may be eligible for a pro-rata credit or refund.</li>
      </ul>
      <h3>4. Non-Refundable Circumstances</h3>
      <ul>
        <li>Forgetting to cancel before the renewal date.</li>
        <li>Not using the platform during a billing period.</li>
        <li>Requests made more than 30 days after a charge.</li>
      </ul>
      <p>Circumstances vary, reach out and we'll consider unusual situations case by case.</p>
      <h3>5. Australian Consumer Law</h3>
      <p>Nothing in this policy limits your rights under the <strong>Australian Consumer Law (ACL)</strong>. For more information, visit accc.gov.au.</p>
      <h3>6. How to Request a Refund</h3>
      <p>Email us with your account email, venue name, date of charge, and reason. We aim to respond within 2 business days. Approved refunds are processed via Stripe and typically appear within 5–10 business days.</p>
      <h3>7. Contact</h3>
      <div className="legal-contact-box">
        <p><strong>Email:</strong> hello@feja.com.au</p>
        <p><strong>Location:</strong> Victoria, Australia</p>
      </div>
    </>
  );
}

function NumPad({ value, threshold, buffer, onValue, onDone, inline }) {
  const liveResult = threshold && value !== '' ? getResult(threshold, value, buffer) : null;
  const press = (char) => {
    if (char === '⌫') { onValue(value.slice(0, -1)); return; }
    if (char === '.') { if (!value.includes('.')) onValue(value + '.'); return; }
    if (char === '−') { onValue(value.startsWith('-') ? value.slice(1) : '-' + value); return; }
    onValue(value + char);
  };

  const inlineStyle = inline ? { flexShrink: 0, position: 'static', transform: 'none', width: '100%', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.1)', borderTop: 'none', padding: '12px 16px', background: 'var(--surf)' } : {};

  const pad = (
    <div className={`numpad${inline ? ' numpad--inline' : ''}`} style={inlineStyle} onPointerDown={e => e.stopPropagation()}>
      <div className={`numpad-display${liveResult ? ` numpad-display--${liveResult}` : ''}`}>
        {value !== '' ? `${value}°C` : '-'}
      </div>
      <div className="numpad-grid">
        {['7','8','9','4','5','6','1','2','3','.','0','⌫'].map(k => (
          <button key={k} className={`numpad-key${k==='⌫'?' numpad-key--del':''}`} onPointerDown={() => press(k)}>
            {k}
          </button>
        ))}
      </div>
      <div className="numpad-bottom">
        <button className="numpad-key numpad-key--neg" onPointerDown={() => press('−')}>±</button>
        <button className="numpad-done" onPointerDown={onDone}>Done</button>
      </div>
    </div>
  );

  if (inline) return pad;
  return (
    <div className="numpad-backdrop" onPointerDown={onDone}>
      {pad}
    </div>
  );
}

function StaffChecklist({ onSignOut, user, venue, hideHeader, sections: propSections, onDashboard, onSettings, onProgress, checkFrequency }) {
  const [loadedSections, setLoadedSections] = useState(null);
  const rawSections = (propSections != null && hasSections(propSections)) ? propSections : (hasSections(loadedSections) ? loadedSections : LOG_SECTIONS);
  const groupedMode = isGrouped(rawSections);
  const sections = groupedMode ? flattenSections(rawSections) : (Array.isArray(rawSections) ? rawSections : LOG_SECTIONS);
  const [draftTemp, setDraftTemp] = useState({});
  const [logged, setLogged] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [numpadKey, setNumpadKey] = useState(null);
  const [flashKey, setFlashKey] = useState(null);
  const [dynamicItemName, setDynamicItemName] = useState({});
  const [todayLogs, setTodayLogs] = useState([]);
  const [weekLogs, setWeekLogs] = useState([]);
  const [showDelivery, setShowDelivery] = useState(false);
  const displayName = user?.user_metadata?.name || user?.email?.split('@')[0] || 'there';

  const getShift = (date = new Date()) => {
    const h = date.getHours();
    const shifts = getShiftConfig(checkFrequency || '2x');
    const match = shifts.find(s => h >= s.startH && h < s.endH);
    return match?.label || shifts[0]?.label || 'Day';
  };

  const getShiftKey = useCallback(() => {
    const now = new Date();
    const d = now.toLocaleDateString('en-AU');
    return `feja_logged_${d}_${getShift(now)}`;
  }, []);

  const currentShift = getShift();

  useEffect(() => {
    if ((propSections != null && hasSections(propSections)) || !venue?.id) return;
    supabase.from('venues').select('sections').eq('id', venue.id).single()
      .then(({ data }) => {
        if (hasSections(data?.sections)) {
          setLoadedSections(data.sections);
        }
      });
  }, [venue?.id, propSections]);

  useEffect(() => {
    const stored = localStorage.getItem(getShiftKey());
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      const migrated = Object.fromEntries(
        Object.entries(parsed).filter(([, v]) => typeof v === 'object' && v !== null)
      );
      setLogged(migrated);
    } catch {}
  }, [getShiftKey]);

  // Reconcile with Supabase — if localStorage is empty (cleared cache, new device)
  // but logs exist in the DB for this shift, restore them so the checklist shows correct state.
  useEffect(() => {
    if (!user?.id || !venue?.id) return;
    const now = new Date();
    const h = now.getHours();
    const shiftStart = new Date(now);
    if (h >= 5 && h < 11) shiftStart.setHours(5, 0, 0, 0);
    else if (h >= 11 && h < 15) shiftStart.setHours(11, 0, 0, 0);
    else if (h >= 15 && h < 22) shiftStart.setHours(15, 0, 0, 0);
    else {
      shiftStart.setHours(22, 0, 0, 0);
      if (h < 5) shiftStart.setDate(shiftStart.getDate() - 1);
    }
    supabase
      .from('logs')
      .select('*')
      .eq('user_id', user.id)
      .eq('venue_id', venue.id)
      .gte('logged_at', shiftStart.toISOString())
      .order('logged_at', { ascending: false })
      .then(({ data }) => {
        if (!data?.length) return;
        setLogged(prev => {
          const merged = { ...prev };
          data.forEach(log => {
            const key = `${log.section_id}__${log.item}`;
            if (!merged[key]) {
              merged[key] = {
                id: log.id,
                temp: log.temp != null ? String(log.temp) : '',
                result: log.result,
                note: log.note || '',
                actions: log.actions || [],
                loggedAt: log.logged_at,
              };
            }
          });
          localStorage.setItem(getShiftKey(), JSON.stringify(merged));
          return merged;
        });
      });
  }, [user?.id, venue?.id, getShiftKey]);

  useEffect(() => {
    if (!venue?.id) return;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    supabase.from('logs').select('section_id, result, logged_at, actions')
      .eq('venue_id', venue.id)
      .gte('logged_at', today.toISOString())
      .then(({ data }) => {
        if (data) setTodayLogs(data.map(l => ({
          sectionId: l.section_id,
          result: l.result,
          actions: l.actions || [],
          hour: new Date(l.logged_at).getHours(),
        })));
      });
  }, [venue?.id]);

  useEffect(() => {
    if (!venue?.id) return;
    const sixDaysAgo = new Date();
    sixDaysAgo.setDate(sixDaysAgo.getDate() - 6);
    sixDaysAgo.setHours(0, 0, 0, 0);
    supabase.from('logs').select('section_id, result, logged_at')
      .eq('venue_id', venue.id)
      .gte('logged_at', sixDaysAgo.toISOString())
      .then(({ data }) => { if (data) setWeekLogs(data); });
  }, [venue?.id]);

  const handleLog = async (key, section) => {
    const temp = draftTemp[key] ?? '';
    if (temp === '') return;
    const result = getResult(section.threshold, temp, section.buffer ?? getDefaultBuffer(section));
    if (result === null) return;

    setSubmitting(p => ({ ...p, [key]: true }));
    const loggedAt = new Date().toISOString();
    const logId = crypto.randomUUID();

    if (user.id) {
      const { error } = await supabase.from('logs').insert({
        id: logId,
        user_id: user.id,
        user_name: displayName,
        venue_id: venue?.id || null,
        section_id: section.id,
        item: key.split('__')[1],
        temp: parseFloat(temp),
        result,
        note: null,
        actions: [],
        logged_at: loggedAt,
      });
      if (error) { console.error('Log insert error:', error); setSubmitting(p => ({ ...p, [key]: false })); return; }
      if (result === 'fail' && venue?.id && venue?.notification_prefs?.failedTemp !== false) {
        const item = key.split('__')[1];
        const threshText = section.threshold.max !== undefined ? `Max ${section.threshold.max}°C`
          : section.threshold.min !== undefined ? `Min ${section.threshold.min}°C` : '';
        const group = groupedMode ? getSectionGroup(rawSections, section.id) : null;
        const prefix = group ? `${group.name}: ` : '';
        sendPushToAdmins(venue, {
          title: `⚠️ Temperature Alert: ${venue.name || 'Venue'}`,
          body: `${prefix}${item} recorded at ${parseFloat(temp)}°C. Threshold: ${threshText}. Corrective action required.`,
          url: '/',
        });
      }
    }

    setSubmitting(p => ({ ...p, [key]: false }));
    setFlashKey(key);
    setLogged(prev => {
      const next = { ...prev, [key]: { id: logId, temp, result, note: '', actions: [], loggedAt } };
      localStorage.setItem(getShiftKey(), JSON.stringify(next));
      // Fire "all complete" notification if every item is now logged
      if (venue?.id && venue?.notification_prefs?.allComplete) {
        const allItems = [];
        sections.forEach(sec => {
          if (sec.dynamicItems) {
            const loggedForSec = Object.keys(next).some(k => k.startsWith(`${sec.id}__`));
            if (loggedForSec) allItems.push(`${sec.id}__dynamic`);
            else allItems.push(`${sec.id}__dynamic_pending`);
          } else {
            sec.items.forEach(i => allItems.push(`${sec.id}__${i}`));
          }
        });
        const allDone = allItems.every(k =>
          k === key || k.endsWith('__dynamic') || !!next[k]
        ) && !allItems.some(k => k.endsWith('__dynamic_pending'));
        if (allDone) {
          sendPushToAdmins(venue, {
            title: `✅ All checks complete: ${venue.name || 'Venue'}`,
            body: `All ${currentShift} checks have been logged.`,
            url: '/',
          });
        }
      }
      return next;
    });
    setDraftTemp(p => { const n = { ...p }; delete n[key]; return n; });

    let nextKey = null;
    const curGroupKey = openGroupRef.current;
    if (curGroupKey) {
      const curGroup = tileGroups.find(g => g.key === curGroupKey);
      if (curGroup) {
        const allGroupItems = [];
        curGroup.sections.forEach(s => {
          if (!s.dynamicItems) s.items.forEach(i => allGroupItems.push({ key: `${s.id}__${i}` }));
        });
        const idx = allGroupItems.findIndex(x => x.key === key);
        if (idx !== -1) {
          for (let i = idx + 1; i < allGroupItems.length; i++) {
            if (!logged[allGroupItems[i].key]) { nextKey = allGroupItems[i].key; break; }
          }
        }
      }
    } else {
      const tempItems = [];
      sections.filter(s => !s.dynamicItems && Object.keys(s.threshold || {}).length > 0)
        .forEach(sec => sec.items.forEach(item => tempItems.push({ key: `${sec.id}__${item}` })));
      const idx = tempItems.findIndex(i => i.key === key);
      for (let i = idx + 1; i < tempItems.length; i++) {
        if (!logged[tempItems[i].key]) { nextKey = tempItems[i].key; break; }
      }
    }
    setTimeout(() => {
      setFlashKey(null);
      if (nextKey) setNumpadKey(nextKey);
    }, 500);
  };

  const handleSelectAction = async (key, action) => {
    const current = logged[key];
    if (!current) return;
    const nextActions = current.actions.includes(action)
      ? current.actions.filter(a => a !== action)
      : [...current.actions, action];
    setLogged(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev, [key]: { ...prev[key], actions: nextActions } };
      localStorage.setItem(getShiftKey(), JSON.stringify(next));
      return next;
    });
    if (current.id && user?.id) {
      const { error } = await supabase.from('logs').update({ actions: nextActions }).eq('id', current.id);
      if (error) { console.error('Corrective action update failed:', error); return; }
    }
    if (!current.actions.includes(action) && venue?.id && venue?.notification_prefs?.correctiveAction !== false) {
      const item = key.split('__')[1];
      sendPushToAdmins(venue, {
        title: `✅ Corrective Action Logged: ${venue.name || 'Venue'}`,
        body: `${displayName} logged a corrective action for ${item}: "${action}".`,
        url: '/',
      });
    }
  };

  const relog = async (key) => {
    const logId = logged[key]?.id;
    if (logId) {
      const { error } = await supabase.from('logs').delete().eq('id', logId);
      if (error) { console.error('Relog delete failed:', error); return; }
    }
    setLogged(prev => {
      const next = { ...prev };
      delete next[key];
      localStorage.setItem(getShiftKey(), JSON.stringify(next));
      return next;
    });
  };

  const handleTaskComplete = async (key, section, item) => {
    const logId = crypto.randomUUID();
    const loggedAt = new Date().toISOString();
    if (user?.id) {
      const { error } = await supabase.from('logs').insert({
        id: logId, user_id: user.id, user_name: displayName,
        venue_id: venue?.id || null, section_id: section.id,
        item, temp: null, result: 'pass', note: null, actions: [], logged_at: loggedAt,
      });
      if (error) { console.error('Task log insert failed:', error); return; }
    }
    setLogged(prev => {
      const next = { ...prev, [key]: { id: logId, temp: null, result: 'pass', note: '', actions: [], loggedAt } };
      localStorage.setItem(getShiftKey(), JSON.stringify(next));
      return next;
    });
  };

  const [openGroup, setOpenGroup] = useState(null);
  const openGroupRef = React.useRef(null);
  useEffect(() => { openGroupRef.current = openGroup; }, [openGroup]);

  useEffect(() => {
    if (!openGroup) { setNumpadKey(null); return; }
    const group = tileGroups.find(g => g.key === openGroup);
    if (!group) return;
    for (const section of group.sections) {
      if (!section.items || section.dynamicItems) continue;
      for (const item of section.items) {
        const key = `${section.id}__${item}`;
        if (!logged[key]) { setNumpadKey(key); return; }
      }
    }
  }, [openGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!numpadKey) return;
    const t = setTimeout(() => {
      document.getElementById(`chk-item-${numpadKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
    return () => clearTimeout(t);
  }, [numpadKey]);

  const [taskOpen, setTaskOpen] = useState({});
  const [relogPending, setRelogPending] = useState(null);
  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick(n => n + 1), 60000); return () => clearInterval(t); }, []);


  const shiftWindows = getShiftConfig(checkFrequency || '2x').map(s => ({ name: s.label, start: s.startH, end: s.endH }));

  const now = new Date();
  const h = now.getHours();
  const dateStr = now.toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short' });
  const timeStr = now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });

  const activeShift = shiftWindows.find(s => h >= s.start && h < s.end);
  const isInShift = !!activeShift;

  const fmtHour = hh => hh === 12 ? '12pm' : hh < 12 ? `${hh}am` : `${hh - 12}pm`;
  const nextShift = (() => {
    const first = shiftWindows[0];
    if (!first) return null;
    if (shiftWindows.length === 1) return { name: first.name, window: 'tomorrow' };
    if (h < first.start) return { name: first.name, window: `${fmtHour(first.start)} – ${fmtHour(first.end)}` };
    const idx = shiftWindows.findIndex(s => h >= s.start && h < s.end);
    if (idx !== -1 && idx < shiftWindows.length - 1) {
      const ns = shiftWindows[idx + 1];
      return { name: ns.name, window: `${fmtHour(ns.start)} – ${fmtHour(ns.end)}` };
    }
    return { name: first.name, window: 'tomorrow' };
  })();

  const isTempSection = (s) => Object.keys(s.threshold || {}).length > 0;
  const tempSections = sections.filter(isTempSection);
  const taskSections = sections.filter(s => !isTempSection(s));

  const logsForCard = React.useMemo(() => {
    const local = Object.entries(logged)
      .filter(([, v]) => v?.loggedAt)
      .map(([key, v]) => ({
        sectionId: key.split('__')[0],
        result: v.result || 'pass',
        actions: v.actions || [],
        hour: new Date(v.loggedAt).getHours(),
      }));
    const localSectionIds = new Set(local.map(l => l.sectionId));
    const dbOnly = todayLogs.filter(l => !localSectionIds.has(l.sectionId));
    return [...dbOnly, ...local];
  }, [logged, todayLogs]);

  const tileGroups = React.useMemo(() => {
    const groups = {};
    tempSections.forEach(s => {
      const gk = getEquipmentGroup(s);
      if (!groups[gk]) groups[gk] = { key: gk, label: GROUP_META[gk]?.label || gk, sections: [] };
      groups[gk].sections.push(s);
    });
    return Object.values(groups).sort((a, b) => (GROUP_META[a.key]?.order ?? 99) - (GROUP_META[b.key]?.order ?? 99));
  }, [tempSections]);

  const escalatedSections = React.useMemo(() => {
    if (!weekLogs || weekLogs.length === 0) return new Set();
    const escalated = new Set();
    tempSections.forEach(section => {
      let consecutive = 0;
      for (let d = 1; d <= 3; d++) {
        const dayStart = new Date();
        dayStart.setDate(dayStart.getDate() - d);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dayStart);
        dayEnd.setDate(dayEnd.getDate() + 1);
        const dayLogs = weekLogs.filter(l => {
          const lt = new Date(l.logged_at);
          return l.section_id === section.id && lt >= dayStart && lt < dayEnd;
        });
        if (dayLogs.length === 0) break;
        if (dayLogs.some(l => l.result === 'pass')) break;
        if (dayLogs.some(l => l.result === 'monitor')) consecutive++;
        else break;
      }
      if (consecutive >= 3) escalated.add(section.id);
    });
    return escalated;
  }, [weekLogs, tempSections]);

  const dynamicLoggedKeys = (s) => Object.keys(logged).filter(k => k.startsWith(`${s.id}__`));
  const allEmpty = tempSections.every(s => s.items.length === 0) && taskSections.every(s => s.items.length === 0);
  const allComplete = !allEmpty && sections.every(s => {
    if (s.dynamicItems) {
      const keys = dynamicLoggedKeys(s);
      return keys.length > 0 && keys.every(k => { const l = logged[k]; return l && !(l.result === 'fail' && !l.actions?.length); });
    }
    return s.items.every(item => {
      const l = logged[`${s.id}__${item}`];
      return l && !(l.result === 'fail' && !l.actions?.length);
    });
  });

  return (
    <div className={hideHeader ? undefined : 'screen app-screen'}>
      {!hideHeader && (
        <div className="app-hdr">
          <div className="app-hdr-center">
            <div className="hdr-date">{dateStr} · {timeStr}</div>
          </div>
          <div className="app-hdr-bottom">
            <div>
              <div className="hdr-name">{displayName}</div>
              {venue?.name && <div className="hdr-venue">{venue.name}</div>}
            </div>
            <span className="hdr-shift">{currentShift} check</span>
            <button className="signout-pill" onClick={onSignOut}>sign out</button>
          </div>
          {(onDashboard || onSettings || onProgress) && (
            <div className="app-hdr-admin-nav">
              {onDashboard && <button className="adm-nav-btn" onClick={onDashboard}>Dashboard</button>}
              {onSettings && <button className="adm-nav-btn" onClick={onSettings}>Settings</button>}
              {onProgress && <button className="adm-nav-btn" onClick={onProgress}>My Progress</button>}
            </div>
          )}
        </div>
      )}

      <div className="log-body">
        {allEmpty && (
          <div className="log-empty-state">
            <div className="log-empty-icon">📋</div>
            <div className="log-empty-title">Nothing to check yet</div>
            <div className="log-empty-body">Ask an admin to add sections and items in Settings.</div>
          </div>
        )}

        {!allEmpty && (
          <>
            {(allComplete || !isInShift) && (
              <div className="chk-next-shift">
                {allComplete && nextShift?.window === 'tomorrow'
                  ? <><strong>All done for today</strong> · Next check tomorrow</>
                  : allComplete && nextShift
                  ? <>✓ <strong>{activeShift?.name || 'Shift'} done</strong> · Next: {nextShift.name} {nextShift.window}</>
                  : !isInShift && nextShift
                  ? <>Next check: <strong>{nextShift.name}</strong> {nextShift.window}</>
                  : null
                }
              </div>
            )}
            {tempSections.length > 0 && (
              <ShiftStatusCard logs={logsForCard} sections={tempSections} checkFrequency={checkFrequency || '2x'} weekLogs={weekLogs} escalatedSections={escalatedSections} />
            )}
            {venue?.id && (
              <>
                <div className="log-section-divider" />
                <button className="dlv-card" style={{ background: 'var(--blk)' }} onClick={() => setShowDelivery(true)}>
                  <div className="dlv-card-left">
                    <span className="dlv-card-title">Log Delivery</span>
                    <span className="dlv-card-sub">Record a supplier delivery with temps</span>
                  </div>
                  <span className="dlv-card-arrow">+</span>
                </button>
                {showDelivery && <DeliverySheet venueId={venue.id} user={user} onClose={() => setShowDelivery(false)} />}
              </>
            )}
            {tileGroups.length > 0 && (
              <div className="sec-grid">
                {tileGroups.map(group => {
                  const allKeys = group.sections.flatMap(s =>
                    s.dynamicItems
                      ? Object.keys(logged).filter(k => k.startsWith(`${s.id}__`))
                      : (s.items || []).map(i => `${s.id}__${i}`)
                  );
                  const totalItems = group.sections.reduce((acc, s) =>
                    acc + (s.dynamicItems
                      ? Math.max(Object.keys(logged).filter(k => k.startsWith(`${s.id}__`)).length, 1)
                      : (s.items || []).length), 0);
                  const doneKeys = allKeys.filter(k => !!logged[k]);
                  const doneCount = doneKeys.length;
                  const hasUnresolved = doneKeys.some(k => logged[k]?.result === 'fail' && !logged[k]?.actions?.length);
                  const hasEscalated = group.sections.some(s => escalatedSections.has(s.id));
                  const hasSoftMonitor = !hasEscalated && doneKeys.some(k => logged[k]?.result === 'monitor' && !logged[k]?.actions?.length);
                  const tileStatus = hasUnresolved ? 'fail'
                    : hasEscalated ? 'monitor'
                    : doneCount >= totalItems && totalItems > 0 ? 'done'
                    : doneCount > 0 ? 'partial' : 'pending';
                  return (
                    <button key={group.key} className={`sec-tile sec-tile--${tileStatus}`}
                      onClick={() => setOpenGroup(group.key)}>
                      <div className="sec-tile-name">{group.label}</div>
                      <div className={`sec-tile-badge sec-tile-badge--${tileStatus}`}>
                        {tileStatus === 'done' ? '✓ Done'
                          : tileStatus === 'monitor' ? '◉ Monitor'
                          : tileStatus === 'fail' ? '⚠ Action req.'
                          : tileStatus === 'partial' ? `${doneCount} / ${totalItems} logged`
                          : 'Tap to log'}
                      </div>
                      {hasSoftMonitor && tileStatus === 'done' && (
                        <div className="sec-tile-monitor-pill">Monitor</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Group sheet */}
            {openGroup && (() => {
              const group = tileGroups.find(g => g.key === openGroup);
              if (!group) return null;
              const closeGroup = () => { setOpenGroup(null); setNumpadKey(null); };
              const activeSection = numpadKey ? group.sections.find(s => numpadKey.startsWith(`${s.id}__`)) : null;

              const renderSectionItems = (section) => {
                if (section.dynamicItems) {
                  const dynamicKeys = Object.keys(logged).filter(k => k.startsWith(`${section.id}__`));
                  const inputName = dynamicItemName[section.id] ?? '';
                  const draftKey = inputName.trim() ? `${section.id}__${inputName.trim()}` : null;
                  const liveDraft = draftKey ? (draftTemp[draftKey] ?? '') : '';
                  const liveResult = liveDraft !== '' ? getResult(section.threshold, liveDraft, section.buffer ?? getDefaultBuffer(section)) : null;
                  return (
                    <>
                      {dynamicKeys.map(key => {
                        const item = key.split('__').slice(1).join('__');
                        const l = logged[key];
                        const isEscalated = escalatedSections.has(section.id);
                        const isResolved = (l.result === 'fail' || l.result === 'monitor') && l.actions?.length > 0;
                        const isSoftMonitor = l.result === 'monitor' && !isEscalated && !isResolved;
                        const resultClass = l.result === 'pass' || isSoftMonitor ? 'pass'
                          : l.result === 'monitor' && !isResolved ? 'monitor'
                          : isResolved ? 'resolved' : 'fail';
                        return (
                          <div key={key} className={`chk-card chk-card--${resultClass}${flashKey === key ? ' chk-card--flash' : ''}`}>
                            <div className="chk-card-top">
                              <button className="chk-relog" onClick={() => setRelogPending(key)}>↺ Re-log</button>
                              <span className={`chk-pill chk-pill--${resultClass}`}>{l.temp}°C</span>
                            </div>
                            {!isSoftMonitor && (l.result === 'fail' || l.result === 'monitor') && section.correctiveActions?.length > 0 && (
                              <div className="chk-corrective">
                                <div className="chk-corrective-label">{isResolved ? 'Action taken' : 'Select corrective action'}</div>
                                <div className="chk-chips">
                                  {section.correctiveActions.map(action => (
                                    <button key={action} className={`chk-chip${l.actions.includes(action) ? ' chk-chip--on' : ''}`} onClick={() => handleSelectAction(key, action)}>{action}</button>
                                  ))}
                                </div>
                              </div>
                            )}
                            {relogPending === key && (
                              <div className="chk-relog-confirm">
                                <span className="chk-relog-warn">Removes this reading</span>
                                <button className="chk-relog-cancel" onClick={() => setRelogPending(null)}>Cancel</button>
                                <button className="chk-relog-go" onClick={() => { relog(key); setRelogPending(null); }}>Re-log</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <div className="chk-card chk-card--pending chk-card--dynamic-add">
                        <input className="chk-dynamic-name-input" placeholder="What are you checking? e.g. Chicken" value={inputName}
                          onChange={e => setDynamicItemName(p => ({ ...p, [section.id]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter' && inputName.trim() && liveDraft !== '') handleLog(draftKey, section); }}
                        />
                        <div className="chk-input-row">
                          <input className={`chk-input${liveResult ? ` chk-input--${liveResult}` : ''}`} type="number" inputMode="decimal" placeholder="°C"
                            value={liveDraft} disabled={!inputName.trim()}
                            onChange={e => draftKey && setDraftTemp(p => ({ ...p, [draftKey]: e.target.value }))}
                            onKeyDown={e => e.key === 'Enter' && liveDraft !== '' && handleLog(draftKey, section)}
                          />
                          <button className="chk-log-btn" disabled={!inputName.trim() || liveDraft === '' || (draftKey && submitting[draftKey])} onClick={() => handleLog(draftKey, section)}>
                            {draftKey && submitting[draftKey] ? '…' : 'Log'}
                          </button>
                        </div>
                      </div>
                    </>
                  );
                }

                return section.items.map(item => {
                  const key = `${section.id}__${item}`;
                  const l = logged[key];
                  const draft = draftTemp[key] ?? '';
                  const liveResult = draft !== '' ? getResult(section.threshold, draft, section.buffer ?? getDefaultBuffer(section)) : null;
                  const isActive = numpadKey === key;

                  if (l) {
                    const isEscalated = escalatedSections.has(section.id);
                    const isResolved = (l.result === 'fail' || l.result === 'monitor') && l.actions?.length > 0;
                    const isSoftMonitor = l.result === 'monitor' && !isEscalated && !isResolved;
                    const resultClass = l.result === 'pass' || isSoftMonitor ? 'pass'
                      : l.result === 'monitor' && !isResolved ? 'monitor'
                      : isResolved ? 'resolved' : 'fail';
                    return (
                      <div key={key} className={`chk-card chk-card--${resultClass}${flashKey === key ? ' chk-card--flash' : ''}`}>
                        <div className="chk-card-top">
                          <button className="chk-relog" onClick={() => setRelogPending(key)}>↺ Re-log</button>
                          <span className={`chk-pill chk-pill--${resultClass}`}>{l.temp}°C</span>
                        </div>
                        {!isSoftMonitor && (l.result === 'fail' || l.result === 'monitor') && section.correctiveActions?.length > 0 && (
                          <div className="chk-corrective">
                            <div className="chk-corrective-label">{isResolved ? 'Action taken' : l.result === 'monitor' ? 'Optional: note corrective action' : 'Select corrective action'}</div>
                            <div className="chk-chips">
                              {section.correctiveActions.map(action => (
                                <button key={action} className={`chk-chip${l.actions.includes(action) ? ' chk-chip--on' : ''}`} onClick={() => handleSelectAction(key, action)}>{action}</button>
                              ))}
                            </div>
                          </div>
                        )}
                        {relogPending === key && (
                          <div className="chk-relog-confirm">
                            <span className="chk-relog-warn">Removes this reading</span>
                            <button className="chk-relog-cancel" onClick={() => setRelogPending(null)}>Cancel</button>
                            <button className="chk-relog-go" onClick={() => { relog(key); setRelogPending(null); }}>Re-log</button>
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div key={key} id={`chk-item-${key}`} className={`chk-card chk-card--pending${isActive ? ' chk-card--active' : ''}`} onClick={() => setNumpadKey(key)} style={{ cursor: 'pointer' }}>
                      <div className="chk-card-top">
                        <span className="chk-card-name">{item}</span>
                        <span className={`chk-pill${liveResult ? ` chk-pill--${liveResult}` : ''}`}>
                          {draft !== '' ? `${draft}°C` : 'Tap to log'}
                        </span>
                      </div>
                    </div>
                  );
                });
              };

              return createPortal(
                <div className="sheet-overlay" onClick={closeGroup}>
                  <div className="sheet sheet--section" style={{ maxHeight: '88dvh', paddingBottom: 0, display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                    <div className="sheet-drag-handle" style={{ flexShrink: 0 }} />
                    <div className="sheet-hdr" style={{ flexShrink: 0 }}>
                      <div>
                        <div className="sheet-title">{group.label}</div>
                        <div className="sheet-subtitle">
                          {(() => {
                            const totalItems = group.sections.reduce((n, s) => n + (s.items || []).length, 0);
                            const s = group.sections[0];
                            const thresh = group.sections.length === 1 && s?.threshold
                              ? (s.threshold.max !== undefined ? `Max ${s.threshold.max}°C` : s.threshold.min !== undefined ? `Min ${s.threshold.min}°C` : null)
                              : null;
                            return thresh ? `${thresh} · ${totalItems} item${totalItems !== 1 ? 's' : ''}` : `${totalItems} item${totalItems !== 1 ? 's' : ''}`;
                          })()}
                        </div>
                      </div>
                      <button className="sheet-close" onClick={closeGroup}>✕</button>
                    </div>
                    <div className="sheet-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                      {group.sections.map((section, idx) => {
                        const thresholdText = section.threshold?.max !== undefined ? `Max ${section.threshold.max}°C`
                          : section.threshold?.min !== undefined ? `Min ${section.threshold.min}°C` : null;
                        return (
                          <div key={section.id} className={`sec-sheet-section${idx > 0 ? ' sec-sheet-section--divider' : ''}`}>
                            {group.sections.length > 1 && (
                            <div className="sec-sheet-section-hdr">
                              <span className="sec-sheet-section-name">{section.title}</span>
                              {thresholdText && <span className="sec-sheet-section-threshold">{thresholdText}</span>}
                            </div>
                            )}
                            {escalatedSections.has(section.id) && (
                              <div className="sec-sheet-escalation">⚠ 3+ days at Monitor — corrective action required</div>
                            )}
                            {renderSectionItems(section)}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ flexShrink: 0, padding: '8px 12px', paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}>
                      <NumPad
                        inline
                        value={draftTemp[numpadKey] || ''}
                        threshold={activeSection?.threshold}
                        buffer={activeSection ? (activeSection.buffer ?? getDefaultBuffer(activeSection)) : undefined}
                        onValue={val => numpadKey && setDraftTemp(p => ({ ...p, [numpadKey]: val }))}
                        onDone={() => { if (numpadKey && activeSection) { const k = numpadKey; setNumpadKey(null); handleLog(k, activeSection); } }}
                      />
                    </div>
                  </div>
                </div>,
                document.body
              );
            })()}

            {taskSections.filter(s => s.items.length > 0).map(section => {
              const isOpen = !!taskOpen[section.id];
              const doneCount = section.items.filter(item => !!logged[`${section.id}__${item}`]).length;
              const allDone = doneCount === section.items.length;
              return (
                <div key={section.id} className={`chk-task-section${allDone ? ' chk-task-section--done' : ''}`}>
                  <button className="chk-task-hdr" onClick={() => setTaskOpen(p => ({ ...p, [section.id]: !p[section.id] }))}>
                    <span className="chk-task-title">{section.title}</span>
                    <div className="chk-task-hdr-right">
                      <span className={`chk-task-count${allDone ? ' chk-task-count--done' : ''}`}>{doneCount}/{section.items.length}</span>
                      <span className="chk-task-chevron">{isOpen ? '∧' : '∨'}</span>
                    </div>
                  </button>
                  <div className="chk-task-progress">
                    <div className="chk-task-progress-fill" style={{ width: `${section.items.length > 0 ? (doneCount / section.items.length) * 100 : 0}%` }} />
                  </div>
                  {isOpen && (
                    <div className="chk-task-body">
                      {section.items.map(item => {
                        const key = `${section.id}__${item}`;
                        const done = !!logged[key];
                        return (
                          <div key={item} className={`chk-task-item${done ? ' chk-task-item--done' : ''}`}>
                            <span className="chk-task-item-name">{item}</span>
                            {done && relogPending === key ? (
                              <div className="chk-relog-confirm chk-relog-confirm--task">
                                <span className="chk-relog-warn">Undo this?</span>
                                <button className="chk-relog-cancel" onClick={() => setRelogPending(null)}>Cancel</button>
                                <button className="chk-relog-go" onClick={() => { relog(key); setRelogPending(null); }}>Undo</button>
                              </div>
                            ) : (
                              <button
                                className={`chk-task-circle${done ? ' chk-task-circle--done' : ''}`}
                                onClick={() => done ? setRelogPending(key) : handleTaskComplete(key, section, item)}
                                aria-label={done ? 'Done — tap to undo' : 'Mark done'}
                              >
                                {done && <Check size={16} strokeWidth={3} />}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
        </div>

    </div>
  );
}

function getShiftConfig(freq) {
  if (freq === '4x') return [
    { id: 'breakfast', label: 'Breakfast',  startH: 5,  endH: 11 },
    { id: 'lunch',     label: 'Lunch',      startH: 11, endH: 15 },
    { id: 'afternoon', label: 'Afternoon',  startH: 15, endH: 18 },
    { id: 'dinner',    label: 'Dinner',     startH: 18, endH: 23 },
  ];
  if (freq === '3x') return [
    { id: 'morning',  label: 'Morning',  startH: 5,  endH: 11 },
    { id: 'midday',   label: 'Midday',   startH: 11, endH: 16 },
    { id: 'evening',  label: 'Evening',  startH: 16, endH: 23 },
  ];
  if (freq === '2x') return [
    { id: 'am', label: 'AM', startH: 0,  endH: 12 },
    { id: 'pm', label: 'PM', startH: 12, endH: 24 },
  ];
  return [{ id: 'day', label: 'Daily', startH: 0, endH: 24 }];
}

function DeliverySheet({ venueId, user, onClose }) {
  const [supplier, setSupplier] = useState('');
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [itemSuggestions, setItemSuggestions] = useState([]);
  const [items, setItems] = useState([]);
  const [outcome, setOutcome] = useState('accepted');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const supplierRef = React.useRef(null);

  useEffect(() => {
    setTimeout(() => supplierRef.current?.focus(), 400);
  }, []);

  useEffect(() => {
    if (!venueId) return;
    supabase.from('delivery_logs').select('supplier')
      .eq('venue_id', venueId)
      .order('logged_at', { ascending: false })
      .limit(200)
      .then(({ data }) => {
        if (data) setSupplierSuggestions([...new Set(data.map(r => r.supplier))]);
      });
  }, [venueId]);

  useEffect(() => {
    if (!supplier.trim() || !venueId) { setItemSuggestions([]); return; }
    supabase.from('delivery_logs').select('items')
      .eq('venue_id', venueId)
      .ilike('supplier', supplier.trim())
      .order('logged_at', { ascending: false })
      .limit(30)
      .then(({ data }) => {
        if (!data) return;
        const seen = new Map();
        data.forEach(r => (r.items || []).forEach(i => {
          if (!seen.has(i.name)) seen.set(i.name, i.type || 'cold');
        }));
        setItemSuggestions([...seen.entries()].map(([name, type]) => ({ name, type })));
      });
  }, [supplier, venueId]);

  const addItem = (name = '', type = 'cold') => {
    setItems(prev => [...prev, { id: crypto.randomUUID(), name, type, temp: '', result: null }]);
  };

  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const next = { ...item, [field]: value };
      const t = parseFloat(field === 'temp' ? value : next.temp);
      const typ = field === 'type' ? value : next.type;
      if (typ === 'ambient') next.result = 'pass';
      else if (!isNaN(t)) next.result = typ === 'frozen' ? (t <= -15 ? 'pass' : 'fail') : (t <= 5 ? 'pass' : 'fail');
      else next.result = null;
      return next;
    }));
  };

  const canSubmit = !!venueId && supplier.trim() && items.length > 0 &&
    items.every(i => i.name.trim() && (i.type === 'ambient' || i.temp !== ''));

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const displayName = user?.user_metadata?.name || user?.email?.split('@')[0] || 'Staff';
    const { error } = await supabase.from('delivery_logs').insert({
      venue_id: venueId,
      user_id: user?.id,
      user_name: displayName,
      supplier: supplier.trim(),
      items: items.map(({ name, type, temp, result }) => ({
        name: name.trim(), type, temp: temp !== '' ? parseFloat(temp) : null, result: result || 'pass',
      })),
      outcome,
      notes: notes.trim() || null,
    });
    setSubmitting(false);
    if (!error) onClose();
    else console.error('Delivery log failed:', error);
  };

  const filteredSupplierChips = supplierSuggestions.filter(s =>
    !supplier || s.toLowerCase().includes(supplier.toLowerCase())
  ).slice(0, 6);

  const unusedItemSuggestions = itemSuggestions.filter(s => !items.some(i => i.name === s.name));

  return createPortal(
    <div className="sheet-overlay" onClick={onClose}>
      <div className="sheet" onClick={e => e.stopPropagation()}>
        <div className="sheet-hdr">
          <span className="sheet-title">Log Delivery</span>
          <button className="sheet-close" onClick={onClose}>✕</button>
        </div>

        <div className="sheet-body">
          <div className="dlv-section">
            <div className="dlv-label">Supplier</div>
            <input
              ref={supplierRef}
              className="dlv-input"
              placeholder="e.g. Bidvest, PFD Foods…"
              value={supplier}
              onChange={e => setSupplier(e.target.value)}
            />
            {filteredSupplierChips.length > 0 && (
              <div className="dlv-chips">
                {filteredSupplierChips.map(s => (
                  <button key={s} className={`dlv-chip${supplier === s ? ' dlv-chip--on' : ''}`} onClick={() => setSupplier(s)}>{s}</button>
                ))}
              </div>
            )}
          </div>

          {supplier.trim() && (
            <div className="dlv-section">
              <div className="dlv-label">Items received</div>

              {unusedItemSuggestions.length > 0 && (
                <div className="dlv-chips">
                  {unusedItemSuggestions.slice(0, 8).map(s => (
                    <button key={s.name} className="dlv-chip dlv-chip--add" onClick={() => addItem(s.name, s.type)}>
                      {s.name} +
                    </button>
                  ))}
                </div>
              )}

              {items.map(item => (
                <div key={item.id} className="dlv-item">
                  <div className="dlv-item-hdr">
                    <input
                      className="dlv-item-name"
                      placeholder="Item name"
                      value={item.name}
                      onChange={e => updateItem(item.id, 'name', e.target.value)}
                    />
                    <button className="dlv-item-remove" onClick={() => setItems(p => p.filter(i => i.id !== item.id))}>✕</button>
                  </div>
                  <div className="dlv-item-body">
                    <div className="dlv-type-pills">
                      {[['cold','Cold ≤5°C'],['frozen','Frozen ≤-15°C'],['ambient','Ambient']].map(([t, lbl]) => (
                        <button key={t} className={`dlv-type-pill${item.type === t ? ' dlv-type-pill--on' : ''}`} onClick={() => updateItem(item.id, 'type', t)}>{lbl}</button>
                      ))}
                    </div>
                    {item.type !== 'ambient' && (
                      <div className="dlv-temp-row">
                        <input
                          className="dlv-temp-input"
                          type="text"
                          inputMode="decimal"
                          placeholder="—"
                          value={item.temp}
                          onChange={e => updateItem(item.id, 'temp', e.target.value)}
                        />
                        <span className="dlv-temp-unit">°C</span>
                        {item.result && (
                          <span className={`dlv-result dlv-result--${item.result}`}>
                            {item.result === 'pass' ? '✓ Pass' : '✗ Fail'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <button className="dlv-add-item" onClick={() => addItem()}>+ Add item</button>
            </div>
          )}

          {items.length > 0 && (
            <div className="dlv-section">
              <div className="dlv-label">Decision</div>
              <div className="dlv-outcome-row">
                <button className={`dlv-outcome-btn dlv-outcome-btn--accept${outcome === 'accepted' ? ' dlv-outcome-btn--on' : ''}`} onClick={() => setOutcome('accepted')}>✓ Accept</button>
                <button className={`dlv-outcome-btn dlv-outcome-btn--reject${outcome === 'rejected' ? ' dlv-outcome-btn--on' : ''}`} onClick={() => setOutcome('rejected')}>✗ Reject</button>
              </div>
              <textarea className="dlv-notes" placeholder="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} rows={2} />
            </div>
          )}
        </div>

        <div className="dlv-footer">
          <button className="dlv-submit" disabled={!canSubmit || submitting} onClick={submit}>
            {submitting ? 'Saving…' : 'Log Delivery'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ShiftStatusCard({ logs, sections, checkFrequency, weekLogs, escalatedSections: escalatedProp }) {
  const escalatedSections = escalatedProp || new Set();
  const shifts = getShiftConfig(checkFrequency || '2x');
  const currentH = new Date().getHours();
  const hasAnyFail = logs.some(l => l.result === 'fail' && !l.actions?.length);
  const startedShifts = shifts.filter(sh => currentH >= sh.startH);
  const allStartedShiftsDone = startedShifts.length > 0 && sections.length > 0 && startedShifts.every(sh => {
    const sl = logs.filter(l => l.hour >= sh.startH && l.hour < sh.endH);
    return sections.every(s => sl.some(l => l.sectionId === s.id));
  });
  const hasAnyMonitor = !hasAnyFail && logs.some(l => l.result === 'monitor' && escalatedSections.has(l.sectionId));
  const overallStatus = hasAnyFail ? 'fail'
    : allStartedShiftsDone ? (hasAnyMonitor ? 'monitor' : 'done')
    : logs.length > 0 ? 'progress' : 'pending';
  const overallLabel = { done: 'All clear', monitor: 'Monitor zone', fail: 'Action required', progress: 'In progress', pending: 'Not started' }[overallStatus];

  const dayLetter = d => ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'][d.getDay()];

  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const dayHistory = last7.map((day, idx) => {
    const nextDay = new Date(day);
    nextDay.setDate(nextDay.getDate() + 1);
    const dayData = (weekLogs || []).filter(l => {
      const lt = new Date(l.logged_at);
      return lt >= day && lt < nextDay;
    });
    const loggedIds = new Set(dayData.map(l => l.section_id));
    const done = sections.filter(s => loggedIds.has(s.id)).length;
    const total = sections.length;
    const hasFail = dayData.some(l => l.result === 'fail');
    const hasMonitor = !hasFail && dayData.some(l => l.result === 'monitor' && escalatedSections.has(l.section_id));
    let status;
    if (done === 0) status = 'empty';
    else if (done >= total && total > 0) status = hasFail ? 'fail' : hasMonitor ? 'monitor' : 'done';
    else status = 'partial';
    return { day, status, isToday: idx === 6 };
  });

  let streak = 0;
  for (let i = dayHistory.length - 1; i >= 0; i--) {
    if (dayHistory[i].status === 'done') { streak++; }
    else if (i === dayHistory.length - 1 && dayHistory[i].status === 'partial') { continue; }
    else break;
  }

  return (
    <div className={`dash-summary dash-summary--${overallStatus}`}>
      <div className="shift-rows">
        {shifts.map(shift => {
          const isActive = currentH >= shift.startH && currentH < shift.endH;
          const isPast   = currentH >= shift.endH;
          const sl = logs.filter(l => l.hour >= shift.startH && l.hour < shift.endH);
          const loggedIds = new Set(sl.map(l => l.sectionId));
          const done = sections.filter(s => loggedIds.has(s.id)).length;
          const total = sections.length;
          const hasFail = sl.some(l => l.result === 'fail' && !l.actions?.length);
          const hasMonitor = !hasFail && sl.some(l => l.result === 'monitor' && escalatedSections.has(l.sectionId));
          const shiftStatus = hasFail ? 'fail'
            : done >= total && total > 0 ? (hasMonitor ? 'monitor' : 'complete')
            : isActive ? 'active'
            : isPast && done < total ? 'missed'
            : 'upcoming';
          const badge = {
            complete: 'Complete ✓',
            monitor:  'Monitor ◉',
            fail:     'Action req.',
            active:   `${done} / ${total} logged`,
            missed:   'Not completed',
            upcoming: 'Upcoming',
          }[shiftStatus];
          return (
            <div key={shift.id} className={`shift-row shift-row--${shiftStatus}`}>
              <span className="shift-row-label">{shift.label}</span>
              <span className={`shift-row-badge shift-row-badge--${shiftStatus}`}>{badge}</span>
            </div>
          );
        })}
      </div>
      {weekLogs.length > 0 && (
        <div className="shift-history">
          <div className="shift-history-days">
            {dayHistory.map(({ day, status, isToday }, i) => (
              <div key={i} className="shift-history-day">
                <div className={`shift-history-dot shift-history-dot--${status}${isToday ? ' shift-history-dot--today' : ''}`} />
                <span className={`shift-history-lbl${isToday ? ' shift-history-lbl--today' : ''}`}>{dayLetter(day)}</span>
              </div>
            ))}
          </div>
          {streak >= 2 && (
            <div className="shift-streak">{streak} day streak</div>
          )}
        </div>
      )}
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function TodayDashboard({ sections: rawSections, venueId, checkFrequency }) {
  const sections = flattenSections(rawSections);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [openAttention, setOpenAttention] = useState({});
  const [attentionActions, setAttentionActions] = useState({});
  const [savingAction, setSavingAction] = useState({});
  const [openSection, setOpenSection] = useState(null);
  const [subTab, setSubTab] = useState('temps');
  const [chkCompletions, setChkCompletions] = useState([]);
  const savingRef = React.useRef(new Set());

  const fetchLogs = React.useCallback(() => {
    if (!venueId) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    supabase
      .from('logs')
      .select('*')
      .gte('logged_at', today.toISOString())
      .eq('venue_id', venueId)
      .order('logged_at', { ascending: false })
      .limit(2000)
      .then(({ data, error }) => {
        if (error) { setFetchError(true); setLoading(false); return; }
        if (data) {
          setLogs(prev => {
            const saving = savingRef.current;
            const actionMap = {};
            prev.forEach(l => { actionMap[l.id] = l.actions; });
            return data.map(log => ({
              id: log.id,
              sectionId: log.section_id,
              item: log.item,
              temp: log.temp,
              result: log.result,
              actions: saving.has(log.id) && actionMap[log.id] !== undefined ? actionMap[log.id] : (log.actions || []),
              time: new Date(log.logged_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false }),
              hour: new Date(log.logged_at).getHours(),
              name: log.user_name || '',
            }));
          });
        }
        setLoading(false);
        setFetchError(false);
      });
  }, [venueId]);

  useEffect(() => {
    if (!venueId) return;
    setLoading(true);
    fetchLogs();
    const interval = setInterval(fetchLogs, 30000);
    return () => clearInterval(interval);
  }, [venueId, fetchLogs]);

  useEffect(() => {
    if (!venueId) return;
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    supabase
      .from('checklist_completions')
      .select('section_id, item, frequency, completed_at')
      .eq('venue_id', venueId)
      .gte('completed_at', monthStart.toISOString())
      .then(({ data }) => { if (data) setChkCompletions(data); });
  }, [venueId]);

  const isTempSec = s => Object.keys(s.threshold || {}).length > 0;
  const tempSections = sections.filter(isTempSec);
  const checklistSections = sections.filter(s => !isTempSec(s));

  // Only temp-section logs belong in the dashboard — checklist completions live in checklist_completions
  const tempSectionIds = new Set(tempSections.map(s => s.id));

  // Deduplicate logs: query is DESC by logged_at so first occurrence = most recent per (section, item)
  const uniqueLogs = (() => {
    const seen = new Set();
    return logs.filter(l => {
      if (!tempSectionIds.has(l.sectionId)) return false;
      const key = `${l.sectionId ?? 'null'}__${l.item ?? 'null'}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();
  const visibleSections = subTab === 'temps' ? tempSections : checklistSections;

  // Attention panel shows ALL unresolved fails regardless of active sub-tab —
  // a temp fail must not disappear just because the user switched to Checklists.
  const attentionItems = uniqueLogs.filter(l => l.result === 'fail' && !l.actions?.length);

  // Checklist summary stats
  const chkDayStart = (() => { const d = new Date(); d.setHours(0,0,0,0); return d; })();
  const chkWeekStart = (() => { const d = new Date(chkDayStart); d.setDate(d.getDate() - d.getDay()); return d; })();
  const chkMonthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const chkWindowStart = (freq) => freq === 'monthly' ? chkMonthStart : freq === 'weekly' ? chkWeekStart : chkDayStart;
  const chkItems = checklistSections.flatMap(s =>
    (s.items || []).map(item => ({ sectionId: s.id, item, freq: s.itemFrequencies?.[item] || 'daily' }))
  );
  const chkTotal = chkItems.length;
  const chkDone = chkItems.filter(({ sectionId, item, freq }) =>
    chkCompletions.some(c => c.section_id === sectionId && c.item === item && new Date(c.completed_at) >= chkWindowStart(freq))
  ).length;
  const chkDue = chkTotal - chkDone;
  const prevDayStart = (() => { const d = new Date(chkDayStart); d.setDate(d.getDate() - 1); return d; })();
  const hadYesterdayActivity = chkCompletions.some(c => new Date(c.completed_at) >= prevDayStart && new Date(c.completed_at) < chkDayStart);
  const chkMissed = hadYesterdayActivity
    ? chkItems.filter(({ freq }) => freq === 'daily').filter(({ sectionId, item }) =>
        !chkCompletions.some(c => c.section_id === sectionId && c.item === item && new Date(c.completed_at) >= prevDayStart && new Date(c.completed_at) < chkDayStart)
      ).length
    : 0;
  const chkPct = chkTotal > 0 ? Math.round((chkDone / chkTotal) * 100) : 0;
  const chkStatus = chkMissed > 0 ? 'fail' : chkDue === 0 && chkTotal > 0 ? 'done' : chkDone > 0 ? 'progress' : 'pending';
  const chkLabel = { done: 'All clear', fail: 'Items missed', progress: 'In progress', pending: 'Not started' }[chkStatus];

  const getChkSectionStatus = (section) => {
    const items = section.items || [];
    if (!items.length) return 'pending';
    const doneCount = items.filter(item => {
      const freq = section.itemFrequencies?.[item] || 'daily';
      return chkCompletions.some(c => c.section_id === section.id && c.item === item && new Date(c.completed_at) >= chkWindowStart(freq));
    }).length;
    if (doneCount === 0) return 'pending';
    if (doneCount >= items.length) return 'complete';
    return 'partial';
  };

  const saveAttentionActions = async (logId, actions) => {
    savingRef.current.add(logId);
    setSavingAction(p => ({ ...p, [logId]: true }));
    const { error } = await supabase.from('logs').update({ actions }).eq('id', logId);
    if (error) {
      console.error('Failed to save corrective action:', error);
      savingRef.current.delete(logId);
      setSavingAction(p => ({ ...p, [logId]: false }));
      return;
    }
    setLogs(prev => prev.map(l => l.id === logId ? { ...l, actions } : l));
    savingRef.current.delete(logId);
    setOpenAttention(p => ({ ...p, [logId]: false }));
    setSavingAction(p => ({ ...p, [logId]: false }));
  };

  const getSectionStatus = (section) => {
    const sectionLogs = uniqueLogs.filter(l => l.sectionId === section.id);
    const fails = sectionLogs.filter(l => l.result === 'fail');
    const loggedCount = sectionLogs.length;
    if (fails.length > 0) return fails.some(l => !l.actions?.length) ? 'action' : 'resolved';
    if (loggedCount === 0) return 'pending';
    if (section.dynamicItems) return 'complete';
    if (loggedCount >= section.items.length) return 'complete';
    return 'partial';
  };

  if (!venueId || loading) return <div className="dash-loading">Loading…</div>;
  if (fetchError) return <div className="dash-loading">Couldn't load today's data. Check your connection.</div>;

  return (
    <div className="dash-body">
      {subTab === 'temps' && (
        <ShiftStatusCard logs={logs} sections={tempSections} checkFrequency={checkFrequency} />
      )}

      {subTab === 'checklists' && (
        <div className={`dash-summary dash-summary--${chkStatus}`}>
          <span className={`dash-summary-badge dash-summary-badge--${chkStatus}`}>{chkLabel}</span>
          <div className="dash-summary-body">
            <div className="dash-summary-left">
              <span className="dash-summary-pct">{chkPct}%</span>
              <span className="dash-summary-pct-lbl">completed</span>
            </div>
            <div className="dash-summary-right">
              <div className="dash-summary-stat-row">
                <span className="dash-summary-stat-val">{chkDone} / {chkTotal}</span>
                <span className="dash-summary-stat-lbl">done</span>
              </div>
              {chkMissed > 0 && (
                <div className="dash-summary-stat-row dash-summary-stat-row--fail">
                  <span className="dash-fail-pulse" />
                  <span className="dash-summary-stat-val">{chkMissed}</span>
                  <span className="dash-summary-stat-lbl">missed</span>
                </div>
              )}
            </div>
          </div>
          <div className="dash-summary-bar">
            <motion.div
              className="dash-summary-fill"
              initial={{ width: 0 }}
              animate={{ width: `${chkPct}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            />
          </div>
        </div>
      )}

      <div className="dash-subtabs">
        <button className={`dash-subtab${subTab === 'temps' ? ' dash-subtab--active' : ''}`} onClick={() => setSubTab('temps')}>Temps</button>
        <button className={`dash-subtab${subTab === 'checklists' ? ' dash-subtab--active' : ''}`} onClick={() => setSubTab('checklists')}>Checklists</button>
      </div>

      <div className="today-section-list">
        {[...visibleSections].sort((a, b) => {
          const order = { action: 0, partial: 1, pending: 2, resolved: 3, complete: 4 };
          const fn = subTab === 'checklists' ? getChkSectionStatus : getSectionStatus;
          return (order[fn(a)] ?? 5) - (order[fn(b)] ?? 5);
        }).map(section => {
          const status = subTab === 'checklists' ? getChkSectionStatus(section) : getSectionStatus(section);
          const sectionLogs = uniqueLogs.filter(l => l.sectionId === section.id);
          const lastTempLog = sectionLogs.find(l => l.temp != null);
          const StatusIcon = { complete: CheckCircle2, action: AlertCircle, resolved: AlertCircle, partial: Clock, pending: CircleDashed }[status];
          const isOpen = openSection === section.id;
          return (
            <div key={section.id}
              className={`today-tile today-tile--${status}${isOpen ? ' today-tile--open' : ''}`}
              onClick={() => setOpenSection(isOpen ? null : section.id)}
              style={{ cursor: 'pointer' }}
            >
              <div className="today-tile-main">
                <div className={`today-tile-icon-wrap today-tile-icon-wrap--${status}`}>
                  <StatusIcon size={18} />
                </div>
                <div className="today-tile-info">
                  <span className="today-tile-name">{section.title}</span>
                  <span className="today-tile-lbl">
                    {{ complete: 'Complete', action: 'Action req.', resolved: 'Resolved', partial: 'In progress', pending: 'Pending' }[status]}
                  </span>
                </div>
                {lastTempLog && (
                  <div className="today-tile-temp">
                    <span className={`today-tile-temp-val today-tile-temp-val--${lastTempLog.result}`}>{lastTempLog.temp}°C</span>
                    <span className="today-tile-temp-time">{lastTempLog.time}</span>
                  </div>
                )}
              </div>
              {isOpen && (
                <div className="today-tile-logs" onClick={e => e.stopPropagation()}>
                  {sectionLogs.length === 0 ? (
                    <div className="today-tile-log-empty">Nothing logged yet</div>
                  ) : (
                    sectionLogs.map(l => (
                      <div key={l.id} className={`today-tile-log-row today-tile-log-row--${l.result || 'pass'}`}>
                        <span className="today-tile-log-item">{l.item}</span>
                        <span className="today-tile-log-meta">
                          {l.temp != null ? `${l.temp}°C · ` : ''}
                          <span className={`today-tile-log-result today-tile-log-result--${l.result}`}>
                            {l.result === 'fail' ? '✗ Fail' : '✓ Pass'}
                          </span>
                          {l.name ? ` · ${l.name}` : ''} · {l.time}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {attentionItems.length > 0 && (
        <div className="dash-attention">
          <div className="dash-attention-hdr">
            <span className="dash-attention-title">Needs attention</span>
            <span className="dash-attention-count">{attentionItems.length}</span>
          </div>
          {attentionItems.map(log => {
            const section = sections.find(s => s.id === log.sectionId);
            const isOpen = openAttention[log.id];
            const selected = attentionActions[log.id] || [];
            return (
              <div key={log.id} className="dash-attn-item">
                <span className="dash-attn-name">{log.item}</span>
                <span className="dash-attn-detail">{log.temp != null ? `${log.temp}°C · ` : ''}logged {log.time}</span>
                <span className="dash-attn-label">Corrective action required</span>
                {!isOpen && (
                  <button className="dash-attn-btn" onClick={() => setOpenAttention(p => ({ ...p, [log.id]: true }))}>Add corrective action</button>
                )}
                {isOpen && (
                  <div className="dash-attn-actions">
                    <div className="dash-attn-chips">
                      {(section?.correctiveActions || []).map(action => (
                        <button key={action} className={`log-chip ${selected.includes(action) ? 'log-chip--on' : ''}`}
                          onClick={() => setAttentionActions(p => {
                            const prev = p[log.id] || [];
                            return { ...p, [log.id]: prev.includes(action) ? prev.filter(a => a !== action) : [...prev, action] };
                          })}>
                          {action}
                        </button>
                      ))}
                    </div>
                    <div className="dash-attn-btns">
                      <button className="dash-attn-cancel" onClick={() => setOpenAttention(p => ({ ...p, [log.id]: false }))}>Cancel</button>
                      <button className="dash-attn-save" disabled={!selected.length || savingAction[log.id]} onClick={() => saveAttentionActions(log.id, selected)}>
                        {savingAction[log.id] ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StaffProgressView({ user, venue, sections: rawSections }) {
  const sections = flattenSections(rawSections);
  const staticSections = sections.filter(s => !s.dynamicItems);
  const [logsByDay, setLogsByDay] = useState({});
  const totalItems = staticSections.reduce((n, s) => n + s.items.length, 0);

  const getShift = () => {
    const h = new Date().getHours();
    if (h >= 5 && h < 11) return 'Breakfast';
    if (h >= 11 && h < 15) return 'Lunch';
    if (h >= 15 && h < 22) return 'Dinner';
    return 'Night';
  };

  useEffect(() => {
    const fetch = async () => {
      const since = new Date();
      since.setDate(since.getDate() - 6);
      since.setHours(0, 0, 0, 0);
      let query = supabase.from('logs').select('*')
        .gte('logged_at', since.toISOString())
        .order('logged_at', { ascending: false })
        .limit(500);
      if (venue?.id) query = query.eq('venue_id', venue.id);
      if (user?.id) query = query.eq('user_id', user.id);
      const { data } = await query;
      if (!data) return;
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const grouped = {};
      data.forEach(log => {
        const d = new Date(log.logged_at); d.setHours(0, 0, 0, 0);
        const offset = Math.round((today - d) / 86400000);
        if (offset >= 0 && offset <= 6) {
          if (!grouped[offset]) grouped[offset] = [];
          grouped[offset].push(log);
        }
      });
      setLogsByDay(grouped);
    };
    fetch();
  }, [venue?.id, user?.id]);

  // Deduplicate per day: query is DESC so first occurrence = most recent per (section, item)
  const dedupeDay = (dayLogs) => {
    const seen = new Set();
    return dayLogs.filter(l => {
      const key = `${l.section_id ?? 'null'}__${l.item ?? 'null'}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map(l => ({ ...l, actions: l.actions || [] }));
  };

  const rawTodayLogs = logsByDay[0] || [];
  const todayLogs = dedupeDay(rawTodayLogs);
  const todayLogged = todayLogs.filter(l => staticSections.some(s => s.id === l.section_id)).length;
  const todayFails = todayLogs.filter(l => l.result === 'fail').length;
  const unresolvedFails = todayLogs.filter(l => l.result === 'fail' && !l.actions.length).length;
  const todayPct = totalItems > 0 ? Math.min(100, Math.round((todayLogged / totalItems) * 100)) : 0;
  const allDone = totalItems > 0 && todayLogged >= totalItems && unresolvedFails === 0;
  const summaryStatus = allDone ? 'done' : unresolvedFails > 0 ? 'fail' : todayLogged > 0 ? 'progress' : 'pending';
  const summaryLabel = { done: 'All clear', fail: 'Action required', progress: 'In progress', pending: 'Not started' }[summaryStatus];
  const lastLogTime = rawTodayLogs[0] ? new Date(rawTodayLogs[0].logged_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false }) : null;

  const dayLabel = (offset) => {
    if (offset === 0) return 'Today';
    if (offset === 1) return 'Yesterday';
    const d = new Date(); d.setDate(d.getDate() - offset);
    return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  return (
    <div className="dash-body">
      <div className={`dash-summary dash-summary--${summaryStatus}`}>
        <div className="dash-summary-top">
          <span className="dash-summary-shift">Today · {getShift()}</span>
          <span className={`dash-summary-badge dash-summary-badge--${summaryStatus}`}>{summaryLabel}</span>
        </div>
        <div className="dash-summary-body">
          <div className="dash-summary-left">
            <div className="dash-summary-pct">{todayPct}%</div>
            <div className="dash-summary-pct-lbl">complete</div>
          </div>
          <div className="dash-summary-right">
            <div className="dash-summary-stat-row">
              <span className="dash-summary-stat-val">{todayLogged}</span>
              <span className="dash-summary-stat-lbl">of {totalItems} logged</span>
            </div>
            {todayFails > 0 && (
              <div className="dash-summary-stat-row dash-summary-stat-row--fail">
                <span className="dash-summary-stat-val">{todayFails}</span>
                <span className="dash-summary-stat-lbl">{todayFails === 1 ? 'fail' : 'fails'}</span>
              </div>
            )}
            {lastLogTime && <div className="dash-summary-lastlog">Last logged {lastLogTime}</div>}
          </div>
        </div>
        <div className="dash-summary-bar">
          <div className="dash-summary-fill" style={{ width: `${todayPct}%` }} />
        </div>
      </div>

      <div className="sp-history">
        <div className="sp-history-title">My last 7 days</div>
        {Array.from({ length: 7 }, (_, i) => {
          const dayLogs = dedupeDay(logsByDay[i] || []);
          const count = dayLogs.filter(l => staticSections.some(s => s.id === l.section_id)).length;
          const fails = dayLogs.filter(l => l.result === 'fail').length;
          const pct = totalItems > 0 ? Math.min(100, Math.round((count / totalItems) * 100)) : 0;
          const status = count === 0 ? 'none' : count >= totalItems && fails === 0 ? 'done' : fails > 0 ? 'fail' : 'partial';
          return (
            <div key={i} className={`sp-day-row${i === 0 ? ' sp-day-row--today' : ''}`}>
              <div className="sp-day-label">{dayLabel(i)}</div>
              <div className="sp-day-bar-wrap">
                <div className="sp-day-bar">
                  <div className={`sp-day-fill sp-day-fill--${status}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
              <div className={`sp-day-stat sp-day-stat--${status}`}>
                {count === 0 ? '-' : `${count}/${totalItems}`}
                {fails > 0 && <span className="sp-day-fail-dot" />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BottomNav({ tabs, active, onChange }) {
  return (
    <div className="bottom-nav">
      {tabs.map(tab => (
        <button
          key={tab.id}
          className={`bottom-nav-btn${active === tab.id ? ' bottom-nav-btn--on' : ''}${tab.disabled ? ' bottom-nav-btn--disabled' : ''}`}
          onClick={() => !tab.disabled && onChange(tab.id)}
        >
          {tab.icon}
          <span className="bottom-nav-label">
            {tab.label}
            {tab.pill && <span className="bottom-nav-pill">{tab.pill}</span>}
          </span>
        </button>
      ))}
    </div>
  );
}

const ICON_LOG = (
  <svg width="24" height="24" viewBox="0 0 22 22" fill="none">
    <rect x="3" y="4" width="5" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
    <line x1="11" y1="6.5" x2="19" y2="6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <rect x="3" y="13" width="5" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
    <line x1="11" y1="15.5" x2="19" y2="15.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const ICON_CHECKLISTS = (
  <svg width="24" height="24" viewBox="0 0 22 22" fill="none">
    <circle cx="5" cy="6" r="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M4 6l1 1 2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="10" y1="6" x2="19" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="5" cy="13" r="2" stroke="currentColor" strokeWidth="1.5"/>
    <line x1="10" y1="13" x2="19" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="5" cy="19" r="2" stroke="currentColor" strokeWidth="1.5"/>
    <line x1="10" y1="19" x2="19" y2="19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const ICON_PROGRESS = (
  <svg width="24" height="24" viewBox="0 0 22 22" fill="none">
    <rect x="3" y="13" width="4" height="6" rx="1" fill="currentColor"/>
    <rect x="9" y="8" width="4" height="11" rx="1" fill="currentColor"/>
    <rect x="15" y="4" width="4" height="15" rx="1" fill="currentColor"/>
  </svg>
);
const ICON_DASH = (
  <svg width="24" height="24" viewBox="0 0 22 22" fill="none">
    <rect x="3" y="3" width="7" height="7" rx="2" fill="currentColor"/>
    <rect x="12" y="3" width="7" height="7" rx="2" fill="currentColor"/>
    <rect x="3" y="12" width="7" height="7" rx="2" fill="currentColor"/>
    <rect x="12" y="12" width="7" height="7" rx="2" fill="currentColor"/>
  </svg>
);
const ICON_REPORT = (
  <svg width="24" height="24" viewBox="0 0 22 22" fill="none">
    <rect x="4" y="2" width="14" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <line x1="7" y1="7" x2="15" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="7" y1="11" x2="15" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="7" y1="15" x2="11" y2="15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const STAFF_TABS = [
  { id: 'checklist',   label: 'Log',        icon: ICON_LOG },
  { id: 'checklists',  label: 'Checklists', icon: ICON_CHECKLISTS },
  { id: 'progress',    label: 'Progress',   icon: ICON_PROGRESS },
];
const ADMIN_TABS = [
  { id: 'checklist',  label: 'Log',        icon: ICON_LOG },
  { id: 'checklists', label: 'Checklists', icon: ICON_CHECKLISTS },
  { id: 'dashboard',  label: 'Dashboard',  icon: ICON_DASH },
  { id: 'report',     label: 'Report',     icon: ICON_REPORT, pill: 'Soon', disabled: true },
];

const FREQ_LABEL = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };
const FREQ_BG    = { daily: 'rgba(37,99,235,0.08)', weekly: 'rgba(124,58,237,0.08)', monthly: 'rgba(8,145,178,0.08)' };
const FREQ_COLOR = { daily: '#2563eb', weekly: '#7c3aed', monthly: '#0891b2' };

function isCleaningTaskDueToday(task, today, completions) {
  const taskCompletions = completions.filter(c => c.task_id === task.id);
  const todayStr = today.toISOString().slice(0, 10);
  const dow = today.getDay();

  switch (task.frequency) {
    case 'daily':
      return !taskCompletions.some(c => c.due_date === todayStr);
    case 'specific_days':
      if (!(task.specific_days || []).includes(dow)) return false;
      return !taskCompletions.some(c => c.due_date === todayStr);
    case 'weekly': {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - dow);
      startOfWeek.setHours(0, 0, 0, 0);
      return !taskCompletions.some(c => new Date(c.completed_at) >= startOfWeek);
    }
    case 'fortnightly': {
      const cutoff = new Date(today);
      cutoff.setDate(today.getDate() - 14);
      return !taskCompletions.some(c => new Date(c.completed_at) >= cutoff);
    }
    case 'monthly': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return !taskCompletions.some(c => new Date(c.completed_at) >= startOfMonth);
    }
    case 'quarterly': {
      const q = Math.floor(today.getMonth() / 3);
      const startOfQuarter = new Date(today.getFullYear(), q * 3, 1);
      return !taskCompletions.some(c => new Date(c.completed_at) >= startOfQuarter);
    }
    default:
      return false;
  }
}

function isCleaningTaskOverdue(task, today, completions) {
  if (!['daily', 'specific_days'].includes(task.frequency)) return false;
  const taskCompletions = completions.filter(c => c.task_id === task.id);
  for (let i = 1; i <= 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const dStr = d.toISOString().slice(0, 10);
    const isDueDay = task.frequency === 'daily' || (task.specific_days || []).includes(d.getDay());
    if (!isDueDay) continue;
    // Found the most recent due day — if no completion for it, it's overdue
    return !taskCompletions.some(c => c.due_date === dStr);
  }
  return false;
}

function ChecklistsView({ sections: rawSections, user, venue }) {
  const sections = React.useMemo(
    () => flattenSections(rawSections).filter(s => Object.keys(s.threshold || {}).length === 0),
    [rawSections]
  );
  const [logged, setLogged] = useState({});
  const [openSections, setOpenSections] = useState({});
  const [cleaningTasks, setCleaningTasks] = useState([]);
  const [cleaningCompletions, setCleaningCompletions] = useState([]);
  const [completingTask, setCompletingTask] = useState(null);
  const [taskNotes, setTaskNotes] = useState({});

  // Checklist key is date-only — not shift-based — so daily/weekly/monthly items
  // don't reset mid-day when the shift boundary rolls over.
  const getKey = () => `feja_chk_${new Date().toLocaleDateString('en-AU')}`;

  useEffect(() => {
    try {
      const stored = localStorage.getItem(getKey());
      if (stored) setLogged(JSON.parse(stored));
    } catch {}
  }, [venue?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user?.id || !venue?.id) return;

    const now = new Date();
    const dayStart = new Date(now); dayStart.setHours(0, 0, 0, 0);
    const weekStart = new Date(dayStart); weekStart.setDate(dayStart.getDate() - dayStart.getDay());
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    supabase
      .from('checklist_completions')
      .select('id, section_id, item, frequency, completed_at')
      .eq('venue_id', venue.id)
      .gte('completed_at', monthStart.toISOString())
      .order('completed_at', { ascending: false })
      .limit(500)
      .then(({ data }) => {
        if (!data?.length) return;
        setLogged(prev => {
          const merged = { ...prev };
          data.forEach(log => {
            const key = `${log.section_id}__${log.item}`;
            if (merged[key]) return;
            const freq = log.frequency || 'daily';
            const windowStart = freq === 'monthly' ? monthStart : freq === 'weekly' ? weekStart : dayStart;
            if (new Date(log.completed_at) >= windowStart) {
              merged[key] = { id: log.id, loggedAt: log.completed_at };
            }
          });
          localStorage.setItem(getKey(), JSON.stringify(merged));
          return merged;
        });
      });
  }, [user?.id, venue?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!venue?.id) return;
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    supabase.from('cleaning_tasks').select('*').eq('venue_id', venue.id).eq('is_active', true).order('sort_order').order('created_at')
      .then(({ data }) => { if (data) setCleaningTasks(data); });
    supabase.from('cleaning_completions').select('*').eq('venue_id', venue.id).gte('completed_at', ninetyDaysAgo.toISOString())
      .then(({ data }) => { if (data) setCleaningCompletions(data); });
  }, [venue?.id]);

  const handleCompleteCleaningTask = async (task) => {
    if (!user?.id || !venue?.id) return;
    setCompletingTask(task.id);
    const today = new Date().toISOString().slice(0, 10);
    const rec = {
      task_id: task.id, venue_id: venue.id, user_id: user.id,
      user_name: user?.user_metadata?.name || user?.email?.split('@')[0] || '',
      due_date: today, notes: taskNotes[task.id] || null,
    };
    const { data, error } = await supabase.from('cleaning_completions').insert(rec).select().single();
    if (!error && data) setCleaningCompletions(prev => [...prev, data]);
    setCompletingTask(null);
    setTaskNotes(p => { const n = { ...p }; delete n[task.id]; return n; });
  };

  const handleComplete = async (key, section, item) => {
    const logId = crypto.randomUUID();
    const loggedAt = new Date().toISOString();
    const freq = section.itemFrequencies?.[item] || 'daily';
    if (user?.id && venue?.id) {
      const { error } = await supabase.from('checklist_completions').insert({
        id: logId, user_id: user.id, venue_id: venue.id,
        section_id: section.id, item, frequency: freq, completed_at: loggedAt,
      });
      if (error) { console.error('Checklist completion insert failed:', error); return; }
    }
    setLogged(prev => {
      const next = { ...prev, [key]: { id: logId, loggedAt } };
      localStorage.setItem(getKey(), JSON.stringify(next));
      return next;
    });
  };

  const handleUncomplete = async (key) => {
    const logId = logged[key]?.id;
    if (logId && user?.id) {
      const { error } = await supabase.from('checklist_completions').delete().eq('id', logId);
      if (error) { console.error('Checklist uncheck failed:', error); return; }
    }
    setLogged(prev => {
      const next = { ...prev };
      delete next[key];
      localStorage.setItem(getKey(), JSON.stringify(next));
      return next;
    });
  };

  const dailySections = sections.filter(s => {
    const type = (s.type || '');
    const title = (s.title || '').toLowerCase();
    return type !== 'delivery' && !title.includes('delivery') && !title.includes('cleaning');
  });

  const dailyTotal = dailySections.reduce((n, s) => n + (s.items || []).length, 0);
  const dailyDone  = dailySections.reduce((n, s) => n + (s.items || []).filter(item => !!logged[`${s.id}__${item}`]).length, 0);
  const dailyPct   = dailyTotal > 0 ? Math.round((dailyDone / dailyTotal) * 100) : 0;
  const dailyStatus = dailyDone >= dailyTotal && dailyTotal > 0 ? 'done' : dailyDone > 0 ? 'progress' : 'pending';
  const dailyLabel  = { done: 'All clear', progress: 'In progress', pending: 'Daily checks not started' }[dailyStatus];

  const today = new Date();
  const cleaningDue = cleaningTasks.filter(t => isCleaningTaskDueToday(t, today, cleaningCompletions));
  const cleaningOverdue = cleaningTasks.filter(t => isCleaningTaskOverdue(t, today, cleaningCompletions));
  const cleaningDoneToday = cleaningTasks.filter(t => {
    const todayStr = today.toISOString().slice(0, 10);
    return cleaningCompletions.some(c => c.task_id === t.id && c.due_date === todayStr);
  });

  if (sections.length === 0) {
    return (
      <div className="log-empty-state">
        <div className="log-empty-icon">✓</div>
        <div className="log-empty-title">No checklists yet</div>
        <div className="log-empty-body">Ask an admin to add checklists in Settings.</div>
      </div>
    );
  }

  const renderSection = (section) => {
    const items = section.items || [];
    const doneCount = items.filter(item => !!logged[`${section.id}__${item}`]).length;
    const allDone = items.length > 0 && doneCount === items.length;
    const isOpen = !!openSections[section.id];
    const toggle = () => setOpenSections(p => ({ ...p, [section.id]: !p[section.id] }));
    return (
      <div key={section.id} className={`chk-section${allDone ? ' chk-section--done' : ''}`}>
        <button className="chk-section-hdr" onClick={toggle}>
          <div className="chk-section-hdr-left">
            <span className="chk-section-title">{section.title}</span>
            <span className={`chk-section-count${allDone ? ' chk-section-count--done' : ''}`}>
              {allDone ? '✓ Done' : `${doneCount} / ${items.length}`}
            </span>
          </div>
          <span className="chk-section-chevron">{isOpen ? '∧' : '∨'}</span>
        </button>
        <div className="chk-section-progress">
          <div className="chk-section-progress-fill" style={{ width: `${items.length > 0 ? (doneCount / items.length) * 100 : 0}%`, background: allDone ? 'var(--grn)' : 'var(--brand)' }} />
        </div>
        {isOpen && (
          <div className="chk-task-list">
            {items.map(item => {
              const key = `${section.id}__${item}`;
              const done = !!logged[key];
              const freq = section.itemFrequencies?.[item];
              return (
                <div key={item} className={`chk-task-row${done ? ' chk-task-row--done' : ''}`}>
                  <button
                    className={`chk-task-circle${done ? ' chk-task-circle--done' : ''}`}
                    aria-label={done ? `Uncheck ${item}` : `Check ${item}`}
                    onClick={() => done ? handleUncomplete(key) : handleComplete(key, section, item)}
                  >
                    {done && <Check size={14} />}
                  </button>
                  <span className={`chk-task-item-name${done ? ' chk-task-item-name--done' : ''}`}>{item}</span>
                  {freq && (
                    <span className="chk-freq-badge" style={{ background: FREQ_BG[freq], color: FREQ_COLOR[freq] }}>
                      {FREQ_LABEL[freq]}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="chk-body">

      {/* Today's Checks progress card */}
      {dailySections.length > 0 && (
        <div className={`dash-summary dash-summary--${dailyStatus}`} style={{ display: 'flex', flexDirection: 'row', padding: 0 }}>
          {/* Left — daily checks */}
          <div style={{ flex: 1, padding: '22px 18px', display: 'flex', flexDirection: 'column', gap: 0 }}>
            <span className={`dash-summary-badge dash-summary-badge--${dailyStatus}`}>{dailyLabel}</span>
            <div className="dash-summary-body">
              <div className="dash-summary-left">
                <span className="dash-summary-pct">{dailyPct}%</span>
                <span className="dash-summary-pct-lbl">completed</span>
              </div>
            </div>
            <div className="dash-summary-bar">
              <motion.div
                className="dash-summary-fill"
                initial={{ width: 0 }}
                animate={{ width: `${dailyPct}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>
            <div className="chk-progress-breakdown">
              {dailySections.map(s => {
                const items = s.items || [];
                const done = items.filter(item => !!logged[`${s.id}__${item}`]).length;
                return (
                  <div key={s.id} className="chk-progress-breakdown-row">
                    <span className="chk-progress-breakdown-name">{s.title}</span>
                    <span className="chk-progress-breakdown-count">{done} / {items.length}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Divider */}
          <div style={{ width: 1, background: 'rgba(255,255,255,0.1)', alignSelf: 'stretch' }} />

          {/* Right — cleaning today */}
          <div style={{ flex: 1, padding: '22px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Cleaning Today</span>
            {cleaningTasks.length === 0 && (
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>No tasks set</span>
            )}
            {cleaningTasks.length > 0 && cleaningOverdue.length > 0 && (
              <span style={{ fontSize: 12, fontWeight: 600, color: '#ff6b4a' }}>{cleaningOverdue.length} overdue</span>
            )}
            {cleaningTasks.length > 0 && cleaningDoneToday.length >= (cleaningDue.length + cleaningDoneToday.length) && cleaningDue.length + cleaningDoneToday.length > 0 && (
              <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>All done ✓</span>
            )}
            {cleaningTasks.length > 0 && cleaningDoneToday.length < (cleaningDue.length + cleaningDoneToday.length) && (cleaningDue.length + cleaningDoneToday.length) > 0 && (
              <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>{cleaningDoneToday.length} / {cleaningDue.length + cleaningDoneToday.length} done</span>
            )}
            {cleaningDue.filter(t => !cleaningCompletions.some(c => c.task_id === t.id && c.due_date === today.toISOString().slice(0,10))).slice(0, 4).map(t => (
              <span key={t.id} style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.4 }}>{t.name}</span>
            ))}
          </div>
        </div>
      )}

      {/* Daily Checks sections */}
      {dailySections.length > 0 && (
        <div className="chk-group">
          <div className="chk-group-hdr">Daily Checks</div>
          {dailySections.map(renderSection)}
        </div>
      )}

      {/* Scheduled Cleaning */}
      <div className="chk-group">
        <div className="chk-group-hdr">Scheduled Cleaning</div>

        {cleaningTasks.length === 0 && (
          <div className="chk-cleaning-card">
            <div className="chk-cleaning-card-left">
              <span className="chk-cleaning-title">No tasks scheduled</span>
              <span className="chk-cleaning-status">Add cleaning tasks in Settings</span>
            </div>
          </div>
        )}

        {cleaningTasks.length > 0 && cleaningOverdue.length > 0 && (
          <div className="chk-cleaning-overdue-banner">
            {cleaningOverdue.length} overdue · {cleaningOverdue.map(t => t.name).join(', ')}
          </div>
        )}

        {cleaningTasks.length > 0 && cleaningDue.length === 0 && cleaningOverdue.length === 0 && (
          <div className="chk-cleaning-card">
            <div className="chk-cleaning-card-left">
              <span className="chk-cleaning-title">Cleaning Schedule</span>
              <span className="chk-cleaning-status">All caught up ✓</span>
            </div>
          </div>
        )}

        {cleaningDue.map(task => {
          const todayStr = today.toISOString().slice(0, 10);
          const done = cleaningCompletions.some(c => c.task_id === task.id && c.due_date === todayStr);
          if (done) return null;
          const isCompleting = completingTask === task.id;
          return (
            <div key={task.id} className="chk-cleaning-task-card">
              <div className="chk-cleaning-task-top">
                <div className="chk-cleaning-task-info">
                  <span className="chk-cleaning-task-name">{task.name}</span>
                  {task.area && <span className="chk-cleaning-task-area">{task.area}</span>}
                  {task.instructions && <span className="chk-cleaning-task-instr">{task.instructions}</span>}
                </div>
                <button
                  className={`chk-cleaning-done-btn${isCompleting ? ' chk-cleaning-done-btn--loading' : ''}`}
                  disabled={isCompleting}
                  onClick={() => handleCompleteCleaningTask(task)}
                >
                  {isCompleting ? '…' : 'Done'}
                </button>
              </div>
            </div>
          );
        })}

        {cleaningDoneToday.length > 0 && (
          <div className="chk-cleaning-completed-list">
            {cleaningDoneToday.map(task => (
              <div key={task.id} className="chk-cleaning-completed-row">
                <span className="chk-cleaning-completed-check">✓</span>
                <span className="chk-cleaning-completed-name">{task.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

function StaffApp({ onSignOut, user, venue }) {
  const [view, setView] = useState('checklist');
  const [sections, setSections] = useState(null);
  const [sectionsLoaded, setSectionsLoaded] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!venue?.id) return;
    supabase.from('venues').select('sections').eq('id', venue.id).single()
      .then(({ data }) => {
        if (data?.sections?.length) setSections(data.sections);
        setSectionsLoaded(true);
      });
  }, [venue?.id]);

  const resolvedSections = sections || [];
  const displayName = user?.user_metadata?.name || user?.email?.split('@')[0] || 'there';
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  const timeStr = now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
  const getShift = () => {
    const h = now.getHours();
    if (h >= 5  && h < 11) return 'Breakfast';
    if (h >= 11 && h < 15) return 'Lunch';
    if (h >= 15 && h < 22) return 'Dinner';
    return 'Night';
  };

  const tempSections = flattenSections(resolvedSections).filter(s => Object.keys(s.threshold || {}).length > 0);

  return (
    <div className="app-layout">
      <div className="app-hdr">
        <div className="app-hdr-center">
          <div className="hdr-date">{dateStr} · {timeStr}</div>
        </div>
        <div className="app-hdr-bottom">
          <div>
            <div className="hdr-name">{displayName}</div>
            {venue?.name && <div className="hdr-venue">{venue.name}</div>}
          </div>
          <span className="hdr-shift">{getShift()} check</span>
          <div className="hdr-actions">
            <button className="hdr-icon-btn" onClick={onSignOut} title="Sign out">
              <svg width="18" height="18" viewBox="0 0 22 22" fill="none"><path d="M15 11H3M15 11l-3-3M15 11l-3 3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 7V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-6a2 2 0 0 1-2-2v-2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
            </button>
          </div>
        </div>
      </div>
      <div className="app-layout-content">
        {!sectionsLoaded && <div className="dash-loading">Loading…</div>}
        {sectionsLoaded && !hasSections(sections) && (
          <div className="setup-empty">
            <div className="setup-empty-icon">📋</div>
            <div className="setup-empty-title">Not set up yet</div>
            <div className="setup-empty-sub">Your admin hasn't completed venue setup. Ask them to finish configuring Feja.</div>
          </div>
        )}
        {sectionsLoaded && hasSections(sections) && view === 'checklist' && (
          <StaffChecklist hideHeader user={user} venue={venue} sections={tempSections.length > 0 ? tempSections : resolvedSections} checkFrequency={venue?.check_frequency || '2x'} />
        )}
        {sectionsLoaded && hasSections(sections) && view === 'checklists' && (
          <ChecklistsView sections={resolvedSections} user={user} venue={venue} />
        )}
        {sectionsLoaded && hasSections(sections) && view === 'progress' && (
          <StaffProgressView user={user} venue={venue} sections={resolvedSections} />
        )}
      </div>
      <BottomNav tabs={STAFF_TABS} active={view} onChange={setView} />
    </div>
  );
}

function PreviewWrapper({ onExit }) {
  const [role, setRole] = useState('admin');
  return (
    <div>
      <div className="preview-bar">
        <div className="preview-tabs">
          <button className={`preview-tab${role === 'admin' ? ' preview-tab--on' : ''}`} onClick={() => setRole('admin')}>Admin</button>
          <button className={`preview-tab${role === 'staff' ? ' preview-tab--on' : ''}`} onClick={() => setRole('staff')}>Team</button>
        </div>
        <button className="preview-exit" onClick={onExit}>✕ Exit</button>
      </div>
      {role === 'admin' && <AdminDashboard onSignOut={onExit} user={MOCK_USER} venue={null} />}
      {role === 'staff' && <StaffApp onSignOut={onExit} user={MOCK_USER} venue={null} />}
    </div>
  );
}

function InstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) return;
    if (localStorage.getItem('feja_install_dismissed')) return;

    const ios = /ipad|iphone|ipod/i.test(navigator.userAgent) &&
      /safari/i.test(navigator.userAgent) &&
      !/crios|fxios|chrome/i.test(navigator.userAgent);

    if (ios) { setIsIOS(true); setVisible(true); return; }

    const handler = e => { e.preventDefault(); _installPrompt = e; setDeferredPrompt(e); setVisible(true); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => { localStorage.setItem('feja_install_dismissed', '1'); setVisible(false); };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setVisible(false);
    setDeferredPrompt(null);
  };

  if (!visible) return null;

  return (
    <div className="install-banner">
      <div className="install-banner-left">
        <div className="install-banner-title">Add Feja to your home screen</div>
        <div className="install-banner-hint">
          {isIOS ? <>Tap <strong>Share</strong> then <strong>Add to Home Screen</strong></> : 'Get quick access from your home screen'}
        </div>
      </div>
      <div className="install-banner-right">
        {!isIOS && <button className="install-banner-add" onClick={install}>Add</button>}
        <button className="install-banner-dismiss" onClick={dismiss}>✕</button>
      </div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function LegacyOnboardingModal({ onConfirm, onSkip, venue }) {
  const [step, setStep] = useState(1);
  const [venueType, setVenueType] = useState(null);
  const [counts, setCounts] = useState({});
  const [extras, setExtras] = useState({});
  const [venueName, setVenueName] = useState(venue?.name || '');
  const [hotelConfig, setHotelConfig] = useState({ kitchens: 1, bar: false, breakfast: false, roomService: false });
  const [groupNames, setGroupNames] = useState({});

  const VENUE_TYPES = [
    { id: 'kitchen', label: 'Kitchen',    sub: 'Restaurant, canteen, food service' },
    { id: 'bar',     label: 'Bar / Pub',  sub: 'Drinks-led, beer lines' },
    { id: 'cafe',    label: 'Café',       sub: 'Coffee shop, light meals' },
    { id: 'hotel',   label: 'Hotel',      sub: 'Multiple kitchens, full service' },
  ];

  const EQUIPMENT = {
    kitchen: [
      { key: 'fridges',  label: 'Fridges',        default: 1, max: 8 },
      { key: 'freezers', label: 'Freezers',        default: 1, max: 4 },
      { key: 'hotHold',  label: 'Hot hold areas',  default: 1, max: 4 },
    ],
    bar: [
      { key: 'glasswashers', label: 'Glass washers', default: 1, max: 4 },
    ],
    cafe: [
      { key: 'displayFridge', label: 'Display fridges', default: 1, max: 4 },
      { key: 'hotHold',       label: 'Hot hold areas',  default: 1, max: 4 },
    ],
    hotel: [],
  };

  const EXTRAS = {
    kitchen: [
      { key: 'cooking',  label: 'Reheating',       default: true },
      { key: 'delivery', label: 'Delivery checks',  default: true },
      { key: 'opening',  label: 'Opening checks',   default: true },
      { key: 'closing',  label: 'Closing checks',   default: true },
    ],
    bar: [
      { key: 'opening', label: 'Opening checks', default: true },
      { key: 'closing', label: 'Closing checks', default: true },
    ],
    cafe: [
      { key: 'opening', label: 'Opening checks', default: true },
      { key: 'closing', label: 'Closing checks', default: true },
    ],
    hotel: [],
  };

  const HOW_IT_WORKS = {
    kitchen: {
      checks: ['Fridge temps', 'Freezer temps', 'Hot holding', 'Cooking temps', 'Deliveries', 'Opening & closing'],
      time: '~2 min per check',
      compliance: 'Keeps you compliant with Standard 3.2.2A',
    },
    bar: {
      checks: ['Fridge temps', 'Beer line checks', 'Glass washer', 'Opening & closing'],
      time: '~1 min per check',
      compliance: 'Keeps your venue audit-ready',
    },
    cafe: {
      checks: ['Display fridge temps', 'Milk fridge', 'Hot food', 'Opening & closing'],
      time: '~2 min per check',
      compliance: 'Keeps you compliant with Standard 3.2.2A',
    },
    hotel: {
      checks: ['Kitchen temps', 'Bar checks', 'Breakfast service', 'Room service', 'Opening & closing'],
      time: '~2 min per department',
      compliance: 'Full HACCP compliance across your property',
    },
  };

  const selectType = (type) => {
    setVenueType(type);
    if (type !== 'hotel') {
      const c = {};
      EQUIPMENT[type].forEach(e => { c[e.key] = e.default; });
      setCounts(c);
      const ex = {};
      EXTRAS[type].forEach(e => { ex[e.key] = e.default; });
      setExtras(ex);
    }
    setHotelConfig({ kitchens: 1, bar: false, breakfast: false, roomService: false });
    setGroupNames({});
  };

  const getHotelPreviewGroups = () => {
    const k = hotelConfig.kitchens;
    const groups = [];
    for (let i = 1; i <= k; i++) {
      const id = `hotel_kitchen_${i}`;
      groups.push({ id, name: groupNames[id] ?? (k === 1 ? 'Kitchen' : `Kitchen ${i}`), sections: ['Fridge', 'Freezer', 'Hot Hold', 'Reheating', 'Delivery Receiving'] });
    }
    if (hotelConfig.bar) {
      const id = 'hotel_bar';
      groups.push({ id, name: groupNames[id] ?? 'Bar', sections: ['Bar Fridge', 'Glass Washer'] });
    }
    if (hotelConfig.breakfast) {
      const id = 'hotel_breakfast';
      groups.push({ id, name: groupNames[id] ?? 'Breakfast', sections: ['Display Fridge', 'Hot Hold'] });
    }
    if (hotelConfig.roomService) {
      const id = 'hotel_roomservice';
      groups.push({ id, name: groupNames[id] ?? 'Room Service', sections: ['Hot Hold', 'Delivery Fridge'] });
    }
    const checksId = 'hotel_checks';
    groups.push({ id: checksId, name: groupNames[checksId] ?? 'Daily Checks', sections: ['Opening Checks', 'Closing Checks'] });
    return groups;
  };

  const buildHotelSections = () => {
    let ts = Date.now();
    const mkSection = (title, type, threshold, items) => ({
      id: `section_${ts++}`, title, type, threshold,
      correctiveActions: SECTION_TYPES[type]?.correctiveActions || [],
      items,
    });
    const k = hotelConfig.kitchens;
    const groups = [];
    for (let i = 1; i <= k; i++) {
      const id = `hotel_kitchen_${i}`;
      groups.push({
        id,
        name: groupNames[id] ?? (k === 1 ? 'Kitchen' : `Kitchen ${i}`),
        sections: [
          mkSection('Fridge', 'cold', { max: 5 }, ['Dairy', 'Meat', 'Seafood', 'Vegetables']),
          mkSection('Freezer', 'cold', { max: -18 }, ['Frozen Meat', 'Frozen Seafood']),
          mkSection('Hot Hold', 'hot', { min: 60 }, ['Soup', 'Cooked Meat', 'Sauces']),
          mkSection('Reheating', 'reheating', { min: 75 }, ['Leftovers', 'Batch Cooked Items']),
          mkSection('Delivery Receiving', 'delivery', { max: 5 }, ['Meat Delivery', 'Dairy Delivery', 'Produce Delivery']),
        ],
      });
    }
    if (hotelConfig.bar) {
      const id = 'hotel_bar';
      groups.push({ id, name: groupNames[id] ?? 'Bar', sections: [
        mkSection('Bar Fridge', 'cold', { max: 5 }, ['Beer', 'Wine', 'Soft Drinks', 'Garnishes']),
        mkSection('Glass Washer', 'custom', {}, ['Rinse temp check', 'Chemical levels']),
      ]});
    }
    if (hotelConfig.breakfast) {
      const id = 'hotel_breakfast';
      groups.push({ id, name: groupNames[id] ?? 'Breakfast', sections: [
        mkSection('Display Fridge', 'cold', { max: 5 }, ['Sandwiches', 'Cakes', 'Salads', 'Dairy']),
        mkSection('Hot Hold', 'hot', { min: 60 }, ['Eggs', 'Bacon', 'Sausages', 'Hash Browns']),
      ]});
    }
    if (hotelConfig.roomService) {
      const id = 'hotel_roomservice';
      groups.push({ id, name: groupNames[id] ?? 'Room Service', sections: [
        mkSection('Hot Hold', 'hot', { min: 60 }, ['Covered Meals', 'Soup']),
        mkSection('Delivery Fridge', 'cold', { max: 5 }, ['Cold Meals', 'Desserts']),
      ]});
    }
    const checksId = 'hotel_checks';
    groups.push({ id: checksId, name: groupNames[checksId] ?? 'Daily Checks', sections: [
      mkSection('Opening Checks', 'custom', {}, ['All fridges at temp', 'Equipment operational', 'Staff briefed']),
      mkSection('Closing Checks', 'custom', {}, ['All food covered and stored', 'Equipment cleaned and off', 'Temps recorded']),
    ]});
    return { grouped: true, groups };
  };

  const adjustCount = (key, delta) => {
    const equip = EQUIPMENT[venueType]?.find(e => e.key === key);
    if (!equip) return;
    setCounts(p => ({ ...p, [key]: Math.min(equip.max, Math.max(0, (p[key] ?? 0) + delta)) }));
  };

  const buildSections = () => {
    const sects = [];
    let ts = Date.now();
    const mk = (title, type, threshold, items) => ({
      id: `section_${ts++}`, title, type, threshold,
      correctiveActions: SECTION_TYPES[type]?.correctiveActions || [],
      items,
    });
    const lbl = (base, total, i) => total === 1 ? base : `${base} ${i}`;

    if (venueType === 'kitchen') {
      for (let i = 1; i <= (counts.fridges || 0); i++)
        sects.push(mk(lbl('Fridge', counts.fridges, i), 'cold', { max: 5 }, ['Dairy', 'Meat', 'Seafood', 'Vegetables']));
      for (let i = 1; i <= (counts.freezers || 0); i++)
        sects.push(mk(lbl('Freezer', counts.freezers, i), 'cold', { max: -18 }, ['Frozen Meat', 'Frozen Seafood', 'Ice Cream']));
      for (let i = 1; i <= (counts.hotHold || 0); i++)
        sects.push(mk(lbl('Hot Hold', counts.hotHold, i), 'hot', { min: 60 }, ['Soup', 'Cooked Meat', 'Sauces']));
      if (extras.cooking)  sects.push(mk('Reheating',          'reheating', { min: 75 }, ['Leftovers', 'Batch Cooked Items']));
      if (extras.delivery) sects.push(mk('Delivery Receiving', 'delivery',  { max: 5 },  ['Meat Delivery', 'Dairy Delivery', 'Produce Delivery']));
      if (extras.opening)  sects.push(mk('Opening Checks',     'custom',    {},           ['Fridge temps checked', 'Freezer temps checked', 'Equipment operational']));
      if (extras.closing)  sects.push(mk('Closing Checks',     'custom',    {},           ['All food covered and stored', 'Equipment cleaned and off', 'Temps recorded']));
    }

    if (venueType === 'bar') {
      sects.push(mk('Beer Fridge',     'cold',   { max: 5 }, ['Kegs', 'Bottled Beer', 'Soft Drinks']));
      sects.push(mk('Back Bar Fridge', 'cold',   { max: 5 }, ['White Wine', 'Rosé', 'Mixers', 'Garnishes']));
      for (let i = 1; i <= (counts.glasswashers || 0); i++)
        sects.push(mk(lbl('Glass Washer', counts.glasswashers, i), 'custom', {}, ['Rinse temp check', 'Chemical levels', 'Cleanliness']));
      if (extras.opening) sects.push(mk('Opening Checks', 'custom', {}, ['Bar fridge temps checked', 'Glass washer operational', 'Beer lines checked']));
      if (extras.closing) sects.push(mk('Closing Checks', 'custom', {}, ['All stock covered', 'Glass washer emptied and cleaned', 'Temps recorded']));
    }

    if (venueType === 'cafe') {
      for (let i = 1; i <= (counts.displayFridge || 0); i++)
        sects.push(mk(lbl('Display Fridge', counts.displayFridge, i), 'cold', { max: 5 }, ['Sandwiches', 'Cakes', 'Salads', 'Dairy']));
      sects.push(mk('Milk Fridge',  'cold', { max: 5 }, ['Full Cream Milk', 'Skim Milk', 'Oat Milk', 'Soy Milk']));
      for (let i = 1; i <= (counts.hotHold || 0); i++)
        sects.push(mk(lbl('Hot Hold', counts.hotHold, i), 'hot', { min: 60 }, ['Soup', 'Savouries', 'Pies']));
      if (extras.opening) sects.push(mk('Opening Checks', 'custom', {}, ['Fridge temps checked', 'Display stocked', 'Equipment operational']));
      if (extras.closing) sects.push(mk('Closing Checks', 'custom', {}, ['All food covered and stored', 'Display cleaned', 'Temps recorded']));
    }

    return sects;
  };

  const next = () => {
    if (step < 5) setStep(s => s + 1);
    else if (venueType === 'hotel') onConfirm(buildHotelSections(), venueName.trim(), venueType);
    else onConfirm(buildSections(), venueName.trim(), venueType);
  };

  const TITLES = venueType === 'hotel'
    ? ['Choose your venue type', 'Here\'s how Feja works for you', 'Tell us about your property', 'Preview your setup', 'Name your venue']
    : ['Choose your venue type', 'Here\'s how Feja works for you', 'Confirm what you have', 'What else do you log?', 'Name your venue'];
  const SUBS = venueType === 'hotel'
    ? [
      "We'll build your compliance setup around your venue.",
      "You'll be up and running in minutes.",
      "We'll generate your department groups from your answers.",
      "Rename any department before confirming.",
      "This is shown to your staff on the app.",
    ]
    : [
      "We'll build your compliance setup around your venue.",
      "You'll be up and running in minutes.",
      "Adjust the counts — you can always change these in settings.",
      "Toggle any that apply to your venue.",
      "This is shown to your staff on the app.",
    ];

  return (
    <div className="ob-overlay">
      <div className="ob-modal">
        <div className="ob-hdr">
          <div className="ob-steps">
            {[1,2,3,4,5].map(s => (
              <div key={s} className={`ob-step-dot ${s === step ? 'ob-step-dot--active' : s < step ? 'ob-step-dot--done' : ''}`} />
            ))}
          </div>
          {venueType === 'hotel' && <div className="ob-hotel-badge">Hotel plan</div>}
          <div className="ob-title">{TITLES[step - 1]}</div>
          <div className="ob-subtitle">{SUBS[step - 1]}</div>
        </div>

        <div className="ob-body">
          {step === 1 && (
            <div className="ob-type-grid">
              {VENUE_TYPES.map(vt => (
                <button key={vt.id} className={`ob-type-card ${venueType === vt.id ? 'ob-type-card--active' : ''}`} onClick={() => selectType(vt.id)}>
                  <div className="ob-type-label">{vt.label}</div>
                  <div className="ob-type-sub">{vt.sub}</div>
                </button>
              ))}
            </div>
          )}

          {step === 2 && venueType && HOW_IT_WORKS[venueType] && (
            <div className="ob-how-it-works">
              <div className="ob-hiw-checks">
                {HOW_IT_WORKS[venueType].checks.map(c => (
                  <span key={c} className="ob-hiw-chip">{c}</span>
                ))}
              </div>
              <div className="ob-hiw-meta">
                <div className="ob-hiw-meta-row">
                  <span className="ob-hiw-meta-icon">⏱</span>
                  <span className="ob-hiw-meta-text">{HOW_IT_WORKS[venueType].time}</span>
                </div>
                <div className="ob-hiw-meta-row">
                  <span className="ob-hiw-meta-icon">✓</span>
                  <span className="ob-hiw-meta-text">{HOW_IT_WORKS[venueType].compliance}</span>
                </div>
              </div>
            </div>
          )}

          {step === 3 && venueType === 'hotel' && (
            <div className="ob-hotel-config">
              <div className="ob-row">
                <span className="ob-row-label">Number of kitchens</span>
                <div className="ob-stepper">
                  <button className="ob-step-btn" onClick={() => setHotelConfig(p => ({ ...p, kitchens: Math.max(1, p.kitchens - 1) }))} disabled={hotelConfig.kitchens <= 1}>−</button>
                  <span className="ob-step-val">{hotelConfig.kitchens >= 4 ? '4+' : hotelConfig.kitchens}</span>
                  <button className="ob-step-btn" onClick={() => setHotelConfig(p => ({ ...p, kitchens: Math.min(4, p.kitchens + 1) }))}>+</button>
                </div>
              </div>
              {[
                { key: 'bar', label: 'Bar' },
                { key: 'breakfast', label: 'Breakfast service' },
                { key: 'roomService', label: 'Room service' },
              ].map(({ key, label }) => (
                <div key={key} className="ob-row">
                  <span className="ob-row-label">{label}</span>
                  <button
                    className={`ob-toggle ${hotelConfig[key] ? 'ob-toggle--on' : ''}`}
                    onClick={() => setHotelConfig(p => ({ ...p, [key]: !p[key] }))}
                  >
                    <div className="ob-toggle-thumb" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {step === 4 && venueType === 'hotel' && (
            <div className="ob-hotel-preview">
              {getHotelPreviewGroups().map(group => (
                <div key={group.id} className="ob-preview-group">
                  <input
                    className="ob-preview-group-name"
                    value={group.name}
                    onChange={e => setGroupNames(p => ({ ...p, [group.id]: e.target.value }))}
                    placeholder="Department name"
                  />
                  <div className="ob-preview-sections">
                    {group.sections.map((s, i) => (
                      <span key={i} className="ob-preview-section-chip">{s}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 3 && venueType && venueType !== 'hotel' && EQUIPMENT[venueType]?.map(e => (
            <div key={e.key} className="ob-row">
              <span className="ob-row-label">{e.label}</span>
              <div className="ob-stepper">
                <button className="ob-step-btn" onClick={() => adjustCount(e.key, -1)} disabled={(counts[e.key] ?? 0) === 0}>−</button>
                <span className="ob-step-val">{counts[e.key] ?? 0}</span>
                <button className="ob-step-btn" onClick={() => adjustCount(e.key, 1)} disabled={(counts[e.key] ?? 0) === e.max}>+</button>
              </div>
            </div>
          ))}

          {step === 4 && venueType && venueType !== 'hotel' && (
            <div className="ob-extras-grid">
              {EXTRAS[venueType].map(e => (
                <button key={e.key} className={`ob-extra-chip ${extras[e.key] ? 'ob-extra-chip--on' : ''}`} onClick={() => setExtras(p => ({ ...p, [e.key]: !p[e.key] }))}>
                  {e.label}
                </button>
              ))}
            </div>
          )}

          {step === 5 && (
            <input
              className="ob-name-input"
              placeholder="e.g. The Crown Hotel"
              value={venueName}
              onChange={e => setVenueName(e.target.value)}
              autoFocus
            />
          )}
        </div>

        <div className="ob-footer">
          <div className="ob-nav">
            {step > 1 && (
              <button className="ob-back-btn" onClick={() => setStep(s => s - 1)}>Back</button>
            )}
            <button className="ob-confirm-btn" onClick={next} disabled={step === 1 && !venueType}>
              {step === 5 ? 'Set up my venue' : 'Next'}
            </button>
          </div>
          {step === 1 && (
            <button className="ob-skip-btn" onClick={onSkip}>Skip for now</button>
          )}
        </div>
      </div>
    </div>
  );
}

function ComplianceDashboard({ venueId, sections: rawSections }) {
  const allSections = flattenSections(rawSections);
  const tempSections = allSections.filter(s => Object.keys(s.threshold || {}).length > 0);
  const chkSections = allSections.filter(s => Object.keys(s.threshold || {}).length === 0);

  const [weekLogs, setWeekLogs] = useState([]);
  const [weekChk, setWeekChk] = useState([]);
  const [cleaningTasks, setCleaningTasks] = useState([]);
  const [cleaningCompletions, setCleaningCompletions] = useState([]);
  const [weekDeliveries, setWeekDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!venueId) return;
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 6);
    weekAgo.setHours(0, 0, 0, 0);
    const monthAgo = new Date();
    monthAgo.setDate(monthAgo.getDate() - 30);
    const monthAgoStr = [monthAgo.getFullYear(), String(monthAgo.getMonth()+1).padStart(2,'0'), String(monthAgo.getDate()).padStart(2,'0')].join('-');
    Promise.all([
      supabase.from('logs').select('section_id, result, logged_at, actions')
        .eq('venue_id', venueId).gte('logged_at', weekAgo.toISOString()),
      supabase.from('checklist_completions').select('section_id, completed_at')
        .eq('venue_id', venueId).gte('completed_at', weekAgo.toISOString()),
      supabase.from('cleaning_tasks').select('*').eq('venue_id', venueId).eq('is_active', true),
      supabase.from('cleaning_completions').select('task_id, due_date, completed_at, has_issue')
        .eq('venue_id', venueId).gte('due_date', monthAgoStr),
      supabase.from('delivery_logs').select('id, supplier, items, outcome, logged_at, user_name')
        .eq('venue_id', venueId).gte('logged_at', weekAgo.toISOString())
        .order('logged_at', { ascending: false }),
    ]).then(([{ data: logs }, { data: chk }, { data: tasks }, { data: clComp }, { data: dlv }]) => {
      setWeekLogs(logs || []);
      setWeekChk(chk || []);
      setCleaningTasks(tasks || []);
      setCleaningCompletions(clComp || []);
      setWeekDeliveries(dlv || []);
      setLoading(false);
    });
  }, [venueId]);

  const toDateStr = d =>
    [d.getFullYear(), String(d.getMonth()+1).padStart(2,'0'), String(d.getDate()).padStart(2,'0')].join('-');

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  function getDayTempStatus(dayStart, dayEnd) {
    if (tempSections.length === 0) return null;
    const tempIds = new Set(tempSections.map(s => s.id));
    const dayLogs = weekLogs.filter(l => {
      const t = new Date(l.logged_at);
      return t >= dayStart && t < dayEnd && tempIds.has(l.section_id);
    });
    if (dayLogs.length === 0) return 'missing';
    if (dayLogs.some(l => l.result === 'fail' && (!l.actions || !l.actions.length))) return 'issue';
    const loggedIds = new Set(dayLogs.map(l => l.section_id));
    return tempSections.every(s => loggedIds.has(s.id)) ? 'complete' : 'partial';
  }

  function getDayCheckStatus(dayStart, dayEnd) {
    if (chkSections.length === 0) return null;
    const dayChk = weekChk.filter(c => {
      const t = new Date(c.completed_at);
      return t >= dayStart && t < dayEnd;
    });
    if (dayChk.length === 0) return 'missing';
    const completedIds = new Set(dayChk.map(c => c.section_id));
    return chkSections.every(s => completedIds.has(s.id)) ? 'complete' : 'partial';
  }

  function wasCleaningTaskDueOn(task, dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const dow = date.getDay();
    // Use completed_at (not due_date) for lookback — consistent with isCleaningTaskDueToday
    const prior = cleaningCompletions.filter(c => c.task_id === task.id && c.completed_at && new Date(c.completed_at) < date);
    switch (task.frequency) {
      case 'daily': return true;
      case 'specific_days': return (task.specific_days || []).includes(dow);
      case 'weekly': {
        const cutoff = new Date(date); cutoff.setDate(date.getDate() - 7);
        return !prior.some(c => new Date(c.completed_at) >= cutoff);
      }
      case 'fortnightly': {
        const cutoff = new Date(date); cutoff.setDate(date.getDate() - 14);
        return !prior.some(c => new Date(c.completed_at) >= cutoff);
      }
      case 'monthly': {
        const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        return !prior.some(c => new Date(c.completed_at) >= startOfMonth);
      }
      case 'quarterly': {
        const q = Math.floor(date.getMonth() / 3);
        const startOfQ = new Date(date.getFullYear(), q * 3, 1);
        return !prior.some(c => new Date(c.completed_at) >= startOfQ);
      }
      default: return false;
    }
  }

  function getDayCleaningStatus(dateStr) {
    if (cleaningTasks.length === 0) return null;
    const dueTasks = cleaningTasks.filter(t => wasCleaningTaskDueOn(t, dateStr));
    if (dueTasks.length === 0) return null;
    const dayComps = cleaningCompletions.filter(c => c.due_date === dateStr);
    if (dayComps.some(c => c.has_issue)) return 'issue';
    if (dayComps.length === 0) return 'missing';
    const doneIds = new Set(dayComps.map(c => c.task_id));
    return dueTasks.every(t => doneIds.has(t.id)) ? 'complete' : 'partial';
  }

  function getOverallStatus(statuses) {
    const s = statuses.filter(Boolean);
    if (!s.length) return null;
    if (s.includes('issue')) return 'issue';
    if (s.includes('missing')) return 'missing';
    if (s.includes('partial')) return 'partial';
    return 'complete';
  }

  const dayData = days.map(d => {
    const dayEnd = new Date(d); dayEnd.setDate(d.getDate() + 1);
    const dateStr = toDateStr(d);
    const tempSt = getDayTempStatus(d, dayEnd);
    const chkSt = getDayCheckStatus(d, dayEnd);
    const cleanSt = getDayCleaningStatus(dateStr);
    const overall = getOverallStatus([tempSt, chkSt, cleanSt]);
    return { date: d, dateStr, tempSt, chkSt, cleanSt, overall };
  });

  const clearDays = dayData.filter(d => d.overall === 'complete').length;
  const partialDays = dayData.filter(d => d.overall === 'partial').length;
  const missingDays = dayData.filter(d => d.overall === 'missing').length;
  const issueDays = dayData.filter(d => d.overall === 'issue').length;

  const today = dayData[0];
  const needsAttention = [];
  if (today.tempSt === 'issue') needsAttention.push('Unresolved temperature fail recorded today');
  if (today.tempSt === 'missing') needsAttention.push('No temperature checks logged today');
  if (today.chkSt === 'missing') needsAttention.push('Checklists not completed today');
  if (today.chkSt === 'partial') needsAttention.push('Checklists partially completed today');
  if (today.cleanSt === 'issue') needsAttention.push('Cleaning issue recorded today');
  if (today.cleanSt === 'missing') needsAttention.push('Cleaning tasks not completed today');
  if (dayData[1]?.tempSt === 'issue') needsAttention.push('Unresolved temperature fail from yesterday');
  if (dayData[1]?.cleanSt === 'issue') needsAttention.push('Cleaning issue unresolved from yesterday');

  const CHIP_ICON = { complete: '✓', partial: '~', missing: '—', issue: '⚠' };

  if (loading) return <div className="dash-loading">Loading…</div>;
  if (!venueId) return null;

  return (
    <div className="compliance-dash">
      <div className="cdash-summary-card">
        <div className="cdash-summary-headline">
          <span className="cdash-summary-big">{clearDays}</span>
          <span className="cdash-summary-of"> / 7 days all clear</span>
        </div>
        <div className="cdash-summary-grid">
          {[
            { val: clearDays, lbl: 'Complete' },
            { val: partialDays, lbl: 'Partial' },
            { val: missingDays, lbl: 'Missing' },
            { val: issueDays, lbl: 'Issues', alert: issueDays > 0 },
          ].map(({ val, lbl, alert }) => (
            <div key={lbl} className="cdash-summary-stat">
              <div className={`cdash-stat-val${alert ? ' cdash-stat-val--alert' : ''}`}>{val}</div>
              <div className="cdash-stat-lbl">{lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {needsAttention.length > 0 && (
        <div className="cdash-card cdash-attention-card">
          <div className="cdash-card-hdr">Needs Attention</div>
          {needsAttention.map((item, i) => (
            <div key={i} className="cdash-attention-item">
              <span className="cdash-attention-dot" />
              {item}
            </div>
          ))}
        </div>
      )}

      <div className="cdash-card">
        <div className="cdash-card-hdr">This Week</div>
        {dayData.map(({ date, dateStr, tempSt, chkSt, cleanSt, overall }) => {
          const isToday = dateStr === toDateStr(new Date());
          const label = isToday ? 'Today'
            : date.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
          const chips = [
            tempSt && { st: tempSt, lbl: 'Temps' },
            chkSt && { st: chkSt, lbl: 'Checks' },
            cleanSt && { st: cleanSt, lbl: 'Cleaning' },
          ].filter(Boolean);
          return (
            <div key={dateStr} className={`cdash-day-row${isToday ? ' cdash-day-row--today' : ''}`}>
              <div className="cdash-day-label">{label}</div>
              <div className={`cdash-day-dot cdash-day-dot--${overall || 'none'}`} />
              <div className="cdash-day-chips">
                {chips.length > 0
                  ? chips.map(({ st, lbl }) => (
                      <span key={lbl} className={`cdash-chip cdash-chip--${st}`}>
                        {CHIP_ICON[st]} {lbl}
                      </span>
                    ))
                  : <span className="cdash-no-data">No data</span>
                }
              </div>
            </div>
          );
        })}
      </div>

      <div className="cdash-card">
        <div className="cdash-card-hdr">Deliveries Received</div>
        {weekDeliveries.length === 0
          ? <div className="cdash-no-data cdash-deliveries-empty">No deliveries logged this week</div>
          : weekDeliveries.map(d => {
              const ts = new Date(d.logged_at);
              const isToday = toDateStr(ts) === toDateStr(new Date());
              const dateLabel = isToday
                ? `Today · ${ts.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })}`
                : ts.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
                  + ' · ' + ts.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
              const hasFail = (d.items || []).some(i => i.result === 'fail');
              const isRejected = d.outcome === 'rejected';
              return (
                <div key={d.id} className="cdash-delivery-row">
                  <div className="cdash-delivery-main">
                    <span className="cdash-delivery-supplier">{d.supplier}</span>
                    {(isRejected || hasFail) && (
                      <span className={`cdash-delivery-badge cdash-delivery-badge--${isRejected ? 'rejected' : 'fail'}`}>
                        {isRejected ? 'Rejected' : 'Temp fail'}
                      </span>
                    )}
                  </div>
                  <div className="cdash-delivery-meta">
                    <span className="cdash-delivery-date">{dateLabel}</span>
                    {d.user_name && <span className="cdash-delivery-by">· {d.user_name}</span>}
                  </div>
                  {d.items && d.items.length > 0 && (
                    <div className="cdash-delivery-items">
                      {d.items.map((item, i) => (
                        <span key={i} className={`cdash-delivery-item${item.result === 'fail' ? ' cdash-delivery-item--fail' : ''}`}>
                          {item.name}{item.temp != null ? ` ${item.temp}°` : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
        }
      </div>
    </div>
  );
}

function AdminDashboard({ onSignOut, user, venue, onVenueUpdate = () => {}, showToast = () => {} }) {
  const [view, setView] = useState('checklist');
  const [sections, setSections] = useState(() => {
    if (!venue?.id) return null;
    try {
      const cached = JSON.parse(localStorage.getItem(`feja_sections_${venue.id}`));
      if (hasSections(cached)) return cached;
    } catch {}
    return null;
  });
  const [sectionsLoading, setSectionsLoading] = useState(() => {
    if (!venue?.id) return true;
    try {
      const cached = JSON.parse(localStorage.getItem(`feja_sections_${venue.id}`));
      if (hasSections(cached)) return false;
    } catch {}
    return true;
  });
  const [collapsed, setCollapsed] = useState({ __items__: true, __team__: true });
  const sectionsLoaded = useRef(false);
  const lastSavedSections = useRef(null);
  const [newItem, setNewItem] = useState({});
  const [newAction, setNewAction] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [team, setTeam] = useState([]);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [settingsTab, setSettingsTab] = useState('items');
  const [addingSection, setAddingSection] = useState(false);
  const [addingToGroup, setAddingToGroup] = useState(null);
  const [openSettingsGroup, setOpenSettingsGroup] = useState(null);
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(null);
  const [newSectionType, setNewSectionType] = useState('cold');
  const [newSectionName, setNewSectionName] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [showNotifSheet, setShowNotifSheet] = useState(false);
  const [showInstallSheet, setShowInstallSheet] = useState(false);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState(
    venue?.notification_prefs || { failedTemp: true, correctiveAction: true, allComplete: false }
  );
  const [, setTick] = useState(0);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('staff');
  const [pendingInvites, setPendingInvites] = useState([]);
  const [cleaningTasks, setCleaningTasks] = useState([]);
  const [cleaningTasksLoaded, setCleaningTasksLoaded] = useState(false);
  const [editingTask, setEditingTask] = useState(null); // null | 'new' | task object
  const [taskDraft, setTaskDraft] = useState({});
  const [savingTask, setSavingTask] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const displayName = user?.user_metadata?.name || user?.email?.split('@')[0] || 'Admin';
  const resolvedSections = sections || [];

  const loadCleaningTasks = () => {
    if (!venue?.id) return;
    supabase
      .from('cleaning_tasks')
      .select('*')
      .eq('venue_id', venue.id)
      .eq('is_active', true)
      .order('sort_order')
      .order('created_at')
      .then(({ data }) => {
        if (data) setCleaningTasks(data);
        setCleaningTasksLoaded(true);
      });
  };

  const saveCleaningTask = async () => {
    if (!taskDraft.name?.trim() || !venue?.id) return;
    setSavingTask(true);
    const payload = {
      venue_id: venue.id,
      name: taskDraft.name.trim(),
      area: taskDraft.area?.trim() || null,
      frequency: taskDraft.frequency || 'daily',
      specific_days: taskDraft.frequency === 'specific_days' ? (taskDraft.specific_days || []) : null,
      instructions: taskDraft.instructions?.trim() || null,
    };
    if (editingTask === 'new') {
      const { data, error } = await supabase.from('cleaning_tasks').insert(payload).select().single();
      if (!error && data) setCleaningTasks(prev => [...prev, data]);
    } else {
      const { data, error } = await supabase.from('cleaning_tasks').update(payload).eq('id', editingTask.id).select().single();
      if (!error && data) setCleaningTasks(prev => prev.map(t => t.id === data.id ? data : t));
    }
    setSavingTask(false);
    setEditingTask(null);
    setTaskDraft({});
  };

  const deleteCleaningTask = async (taskId) => {
    await supabase.from('cleaning_tasks').update({ is_active: false }).eq('id', taskId);
    setCleaningTasks(prev => prev.filter(t => t.id !== taskId));
  };

  const refreshTeam = () => {
    if (!venue?.id) return;
    supabase
      .from('profiles')
      .select('id, name, role')
      .eq('venue_id', venue.id)
      .then(({ data }) => {
        if (data) setTeam(data.map(p => ({ id: p.id, name: p.name || 'Unknown', email: '', role: p.role || 'staff' })));
      });
    supabase
      .from('invites')
      .select('id, email, name, role, created_at, accepted_at')
      .eq('venue_id', venue.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (data) setPendingInvites(data);
      });
  };

  useEffect(() => {
    if (!venue?.id) return;
    refreshTeam();
    supabase
      .from('venues')
      .select('sections')
      .eq('id', venue.id)
      .single()
      .then(({ data }) => {
        let loaded = hasSections(data?.sections) ? data.sections : null;
        if (!loaded) {
          try {
            const backup = JSON.parse(localStorage.getItem(`feja_sections_${venue.id}`));
            if (hasSections(backup)) {
              loaded = backup;
              supabase.from('venues').update({ sections: backup }).eq('id', venue.id)
                .then(({ error }) => { if (error) console.error('Backup sections restore failed:', error); });
            }
          } catch {}
        }
        if (loaded) {
          lastSavedSections.current = loaded;
          setSections(loaded);
        }
        sectionsLoaded.current = true;
        setSectionsLoading(false);
        const dismissed = localStorage.getItem(`feja_ob_${venue.id}`);
        if (!loaded && !dismissed) setShowOnboarding(true);
      });
  }, [venue?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sectionsLoaded.current) return;
    if (sections === null) return;
    if (sections === lastSavedSections.current) return;
    const t = setTimeout(() => {
      if (!venue?.id) return;
      const saving = sections;
      supabase.from('venues').update({ sections: saving }).eq('id', venue.id)
        .then(({ error }) => {
          if (!error) { lastSavedSections.current = saving; showToast('Saved', 'success'); }
          else console.error('Auto-save failed:', error);
        });
    }, 800);
    return () => clearTimeout(t);
  }, [sections, venue?.id]); // eslint-disable-line react-hooks/exhaustive-deps


  const removeMember = async (id) => {
    if (venue?.id) {
      await supabase.from('profiles').update({ venue_id: null }).eq('id', id).eq('venue_id', venue.id);
    }
    setTeam(prev => prev.filter(m => m.id !== id));
    setConfirmRemove(null);
  };

  const toggleSection = id => setCollapsed(p => ({ ...p, [id]: !p[id] }));

  const mapSection = (prev, id, fn) => {
    if (isGrouped(prev)) {
      return { ...prev, groups: prev.groups.map(g => ({ ...g, sections: g.sections.map(s => s.id === id ? fn(s) : s) })) };
    }
    return prev.map(s => s.id === id ? fn(s) : s);
  };

  const updateSection = (id, field, value) =>
    setSections(prev => mapSection(prev, id, s => ({ ...s, [field]: value })));

  const updateThreshold = (id, field, value) =>
    setSections(prev => mapSection(prev, id, s => ({ ...s, threshold: { ...s.threshold, [field]: value === '' ? undefined : parseFloat(value) } })));


  const addItem = (id) => {
    const val = (newItem[id] || '').trim();
    if (!val) return;
    setSections(prev => mapSection(prev, id, s => ({ ...s, items: [...s.items, val] })));
    setNewItem(p => ({ ...p, [id]: '' }));
  };

  const removeItem = (id, idx) =>
    setSections(prev => mapSection(prev, id, s => ({ ...s, items: s.items.filter((_, i) => i !== idx) })));

  const addAction = (id) => {
    const val = (newAction[id] || '').trim();
    if (!val) return;
    setSections(prev => mapSection(prev, id, s => ({ ...s, correctiveActions: [...s.correctiveActions, val] })));
    setNewAction(p => ({ ...p, [id]: '' }));
  };

  const removeAction = (id, idx) =>
    setSections(prev => mapSection(prev, id, s => ({ ...s, correctiveActions: s.correctiveActions.filter((_, i) => i !== idx) })));

  const addSection = () => {
    const template = SECTION_TYPES[newSectionType];
    const id = `section_${Date.now()}`;
    const sectionTitle = newSectionName.trim() || template.label;
    const autoItems = ['cold', 'freezer', 'hot', 'reheating', 'delivery'].includes(newSectionType) ? [sectionTitle] : [];
    const newSection = { id, title: sectionTitle, type: newSectionType, threshold: { ...template.threshold }, correctiveActions: [...template.correctiveActions], items: autoItems };
    if (isGrouped(sections)) {
      const gId = addingToGroup;
      setSections(prev => ({ ...prev, groups: prev.groups.map(g => g.id === gId ? { ...g, sections: [...g.sections, newSection] } : g) }));
    } else {
      setSections(prev => [...(Array.isArray(prev) ? prev : (Array.isArray(resolvedSections) ? resolvedSections : [])), newSection]);
    }
    setAddingSection(false);
    setAddingToGroup(null);
    setNewSectionName('');
    setNewSectionType('cold');
  };

  const addSectionToGroup = (groupKey) => {
    const typeMap = { fridges: 'cold', freezers: 'freezer', hot: 'hot', cooking: 'reheating' };
    const type = typeMap[groupKey] || 'cold';
    const template = SECTION_TYPES[type];
    const id = `section_${Date.now()}`;
    const title = template.label;
    const newSec = { id, title, type, threshold: { ...template.threshold }, correctiveActions: [...template.correctiveActions], items: [title] };
    setSections(prev => [...(Array.isArray(prev) ? prev : []), newSec]);
  };

  const removeSection = (id) => {
    if (isGrouped(sections)) {
      setSections(prev => ({ ...prev, groups: prev.groups.map(g => ({ ...g, sections: g.sections.filter(s => s.id !== id) })) }));
    } else {
      setSections(prev => (Array.isArray(prev) ? prev : []).filter(s => s.id !== id));
    }
  };

  const updateGroupName = (groupId, name) =>
    setSections(prev => ({ ...prev, groups: prev.groups.map(g => g.id === groupId ? { ...g, name } : g) }));

  const addGroup = () => {
    const id = `group_${Date.now()}`;
    setSections(prev => ({ ...prev, groups: [...prev.groups, { id, name: 'New Department', sections: [] }] }));
  };

  const removeGroup = (groupId) => {
    setSections(prev => ({ ...prev, groups: prev.groups.filter(g => g.id !== groupId) }));
    setConfirmDeleteGroup(null);
  };

  // eslint-disable-next-line no-unused-vars
  const handleOnboardingConfirm = (generated, venueName, venueType) => {
    setSections(generated);
    if (venue?.id) {
      localStorage.setItem(`feja_ob_${venue.id}`, '1');
      const updates = {};
      if (venueName) updates.name = venueName;
      if (venueType) updates.venue_type = venueType;
      if (venueType === 'hotel') updates.plan = 'hotel';
      if (Object.keys(updates).length) supabase.from('venues').update(updates).eq('id', venue.id);
    }
    setShowOnboarding(false);
    const alreadyAsked = localStorage.getItem('feja_notif_asked');
    const denied = typeof Notification !== 'undefined' && Notification.permission === 'denied';
    if (!alreadyAsked && !denied) setTimeout(() => setShowNotifSheet(true), 600);
  };

  // eslint-disable-next-line no-unused-vars
  const handleOnboardingSkip = () => {
    if (venue?.id) localStorage.setItem(`feja_ob_${venue.id}`, '1');
    setShowOnboarding(false);
  };

  const handleOnboardingComplete = (generated, venueType, checkFreq) => {
    if (venue?.id) {
      localStorage.setItem(`feja_ob_${venue.id}`, '1');
      const updates = {};
      if (venueType) updates.venue_type = venueType;
      if (checkFreq) updates.check_frequency = checkFreq;
      if (generated?.length) {
        localStorage.setItem(`feja_sections_${venue.id}`, JSON.stringify(generated));
        lastSavedSections.current = generated;
        setSections(generated);
        updates.sections = generated;
      }
      if (Object.keys(updates).length) {
        supabase.from('venues').update(updates).eq('id', venue.id)
          .then(({ error }) => {
            if (error) console.error('Onboarding save failed:', error);
            else if (checkFreq) onVenueUpdate({ check_frequency: checkFreq });
          });
      }
    }
    setShowOnboarding(false);
    const alreadyAsked = localStorage.getItem('feja_notif_asked');
    const denied = typeof Notification !== 'undefined' && Notification.permission === 'denied';
    if (!alreadyAsked && !denied) setTimeout(() => setShowNotifSheet(true), 600);
  };


  const now = new Date();
  const dateStr = now.toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short' });
  const timeStr = now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });

  const trialMsLeft = user?.created_at
    ? Math.max(0, new Date(user.created_at).getTime() + 14 * 86400000 - now.getTime())
    : null;
  const trialDays = trialMsLeft !== null ? Math.floor(trialMsLeft / 86400000) : null;
  const trialHours = trialMsLeft !== null ? Math.floor((trialMsLeft % 86400000) / 3600000) : null;

  const goToSettings = () => {
    setCollapsed(p => {
      const next = { ...p };
      flattenSections(resolvedSections).forEach(s => { next[s.id] = true; });
      return next;
    });
    setView('settings');
  };

  return (
    <div className="app-layout">
      <div className="app-hdr">
        <div className="app-hdr-bottom">
          <div>
            <div className="hdr-name">{displayName}</div>
            {venue?.name && <div className="hdr-venue">{venue.name}</div>}
          </div>
          <div className="hdr-actions">
            <div className="notif-bell-wrap">
              <button className="hdr-icon-btn" onClick={() => setShowNotifDropdown(v => !v)} title="Notifications">
                <svg width="18" height="18" viewBox="0 0 22 22" fill="none"><path d="M11 2a7 7 0 0 0-7 7v3l-1.5 2.5A1 1 0 0 0 3.5 16h15a1 1 0 0 0 .87-1.5L18 12V9a7 7 0 0 0-7-7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><path d="M9 16v.5a2 2 0 0 0 4 0V16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                {trialMsLeft !== null && <span className={`notif-bell-badge${trialMsLeft === 0 || trialDays <= 3 ? ' notif-bell-badge--urgent' : ''}`} />}
              </button>
              {showNotifDropdown && (
                <>
                  <div className="notif-dropdown-overlay" onClick={() => setShowNotifDropdown(false)} />
                  <div className="notif-dropdown">
                    <div className="notif-dropdown-hdr">Notifications</div>
                    {trialMsLeft !== null && (
                      <div className={`notif-item${trialMsLeft === 0 || trialDays <= 3 ? ' notif-item--urgent' : ''}`}>
                        <div className="notif-item-icon">{trialMsLeft === 0 ? '⚠' : '⏱'}</div>
                        <div className="notif-item-body">
                          <div className="notif-item-title">
                            {trialMsLeft === 0 ? 'Trial expired' : trialDays > 0 ? `${trialDays}d ${trialHours}h left on trial` : `${trialHours}h left on trial`}
                          </div>
                          <div className="notif-item-sub">Upgrade to keep access after your trial ends.</div>
                        </div>
                      </div>
                    )}
                    {trialMsLeft === null && (
                      <div className="notif-empty">No new notifications</div>
                    )}
                  </div>
                </>
              )}
            </div>
            <button className="hdr-icon-btn" onClick={goToSettings} title="Settings">
              <svg width="18" height="18" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="11" r="3" stroke="currentColor" strokeWidth="1.6"/><path d="M11 2v2.5M11 17.5V20M2 11h2.5M17.5 11H20M4.93 4.93l1.77 1.77M15.3 15.3l1.77 1.77M4.93 17.07l1.77-1.77M15.3 6.7l1.77-1.77" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
            </button>
            <button className="hdr-icon-btn" onClick={onSignOut} title="Sign out">
              <svg width="18" height="18" viewBox="0 0 22 22" fill="none"><path d="M15 11H3M15 11l-3-3M15 11l-3 3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 7V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-6a2 2 0 0 1-2-2v-2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
            </button>
          </div>
        </div>
      </div>

      <div className="app-layout-content">
        {!showOnboarding && view !== 'settings' && sectionsLoading && (
          <div className="dash-loading">Loading…</div>
        )}
        {!showOnboarding && view !== 'settings' && !sectionsLoading && !hasSections(sections) && (
          <div className="setup-empty">
            <div className="setup-empty-icon">📋</div>
            <div className="setup-empty-title">Your venue isn't set up yet</div>
            <div className="setup-empty-sub">Complete setup to start logging temperature checks and compliance records.</div>
            <button className="setup-empty-btn" onClick={() => setShowOnboarding(true)}>Complete setup →</button>
          </div>
        )}
        {!showOnboarding && view === 'checklist' && !sectionsLoading && hasSections(sections) && (
          <StaffChecklist hideHeader user={user} venue={venue} sections={flattenSections(resolvedSections).filter(s => Object.keys(s.threshold || {}).length > 0)} checkFrequency={venue?.check_frequency || '2x'} />
        )}
        {!showOnboarding && view === 'dashboard' && !sectionsLoading && hasSections(sections) && (
          <ComplianceDashboard venueId={venue?.id} sections={resolvedSections} />
        )}
        {view === 'checklists' && !sectionsLoading && hasSections(sections) && (
          <ChecklistsView sections={resolvedSections} user={user} venue={venue} />
        )}

        {view === 'settings' && (
      <div className="adm-body">

        <div className="adm-tabs">
          <button className={`adm-tab ${settingsTab === 'items' ? 'adm-tab--active' : ''}`} onClick={() => setSettingsTab('items')}>Log Items</button>
          <button className={`adm-tab ${settingsTab === 'team' ? 'adm-tab--active' : ''}`} onClick={() => { setSettingsTab('team'); refreshTeam(); }}>Team</button>
          <button className={`adm-tab ${settingsTab === 'alerts' ? 'adm-tab--active' : ''}`} onClick={() => setSettingsTab('alerts')}>Alerts</button>
          <button className={`adm-tab ${settingsTab === 'cleaning' ? 'adm-tab--active' : ''}`} onClick={() => { setSettingsTab('cleaning'); if (!cleaningTasksLoaded) loadCleaningTasks(); }}>Cleaning</button>
        </div>

        {settingsTab === 'items' && (
          <div className="adm-tab-content">
            {(() => {
              const renderSectionCard = (section) => (
                <div key={section.id} className="adm-card">
                  <div className="adm-card-hdr" onClick={() => toggleSection(section.id)}>
                    <div className="adm-card-hdr-left">
                      <span className="adm-card-title">{section.title}</span>
                      {collapsed[section.id] && (
                        <>
                          <span className="adm-card-summary-rule">
                            {section.type && section.type !== 'custom'
                              ? `${SECTION_TYPES[section.type]?.label} · ${section.threshold.max !== undefined ? `Max ${section.threshold.max}°C` : `Min ${section.threshold.min}°C`}`
                              : [section.threshold.max !== undefined && `Max ${section.threshold.max}°C`, section.threshold.min !== undefined && `Min ${section.threshold.min}°C`].filter(Boolean).join(' · ') || 'Custom'
                            }
                          </span>
                          <span className="adm-card-summary-counts">
                            {!['cold', 'freezer', 'hot'].includes(section.type) && `${section.items.length} item${section.items.length !== 1 ? 's' : ''} · `}{section.correctiveActions.length} corrective action{section.correctiveActions.length !== 1 ? 's' : ''}
                          </span>
                        </>
                      )}
                    </div>
                    <span className={`log-chevron ${collapsed[section.id] ? 'log-chevron--up' : ''}`}>›</span>
                  </div>
                  {!collapsed[section.id] && (
                    <div className="adm-card-body">
                      <div className="adm-group">
                        <div className="adm-group-label">Section name</div>
                        <input className="adm-add-input" value={section.title}
                          onChange={e => updateSection(section.id, 'title', e.target.value)}
                        />
                      </div>
                      {section.type !== 'custom' && (
                      <div className="adm-group">
                        <div className="adm-group-label">Temperature rule</div>
                        <div className="adm-threshold-block">
                          {section.type && (
                            <div className="adm-threshold-type-row">
                              <span className="adm-threshold-lbl">Type</span>
                              <span className="adm-threshold-type-val">{SECTION_TYPES[section.type]?.label}</span>
                            </div>
                          )}
                          <div className="adm-threshold-row">
                            {section.threshold.max !== undefined && (
                              <div className="adm-threshold-field">
                                <span className="adm-threshold-lbl">Max temperature</span>
                                <input className="adm-temp-input" type="number" placeholder=""
                                  value={section.threshold.max ?? ''}
                                  onChange={e => updateThreshold(section.id, 'max', e.target.value)}
                                />
                              </div>
                            )}
                            {section.threshold.min !== undefined && (
                              <div className="adm-threshold-field">
                                <span className="adm-threshold-lbl">Min temperature</span>
                                <input className="adm-temp-input" type="number" placeholder=""
                                  value={section.threshold.min ?? ''}
                                  onChange={e => updateThreshold(section.id, 'min', e.target.value)}
                                />
                              </div>
                            )}
                            <div className="adm-threshold-field">
                              <span className="adm-threshold-lbl">Monitor buffer (°C)</span>
                              <input className="adm-temp-input" type="number" min="0" placeholder={`${getDefaultBuffer(section)}`}
                                value={section.buffer ?? ''}
                                onChange={e => updateSection(section.id, 'buffer', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      )}
                      {section.type === 'custom' && (
                        <div className="adm-group">
                          <div className="adm-group-label">Checklist items</div>
                          {section.items.map((item, idx) => (
                            <div key={idx} className="adm-list-row">
                              <span className="adm-list-item">{item}</span>
                              <button className="adm-remove-btn" onClick={() => removeItem(section.id, idx)}>✕</button>
                            </div>
                          ))}
                          <div className="adm-add-row">
                            <input className="adm-add-input" placeholder="New item name..."
                              value={newItem[section.id] || ''}
                              onChange={e => setNewItem(p => ({ ...p, [section.id]: e.target.value }))}
                              onKeyDown={e => e.key === 'Enter' && addItem(section.id)}
                            />
                            <button className="adm-add-btn" onClick={() => addItem(section.id)}>Add item</button>
                          </div>
                        </div>
                      )}
                      {section.type !== 'custom' && (
                      <div className="adm-group">
                        <div className="adm-group-label">Corrective actions</div>
                        <div className="adm-chips">
                          {section.correctiveActions.map((action, idx) => (
                            <div key={idx} className="adm-chip">
                              <span>{action}</span>
                              <button className="adm-chip-remove" onClick={() => removeAction(section.id, idx)}>✕</button>
                            </div>
                          ))}
                        </div>
                        <div className="adm-add-row">
                          <input className="adm-add-input" placeholder="New action..."
                            value={newAction[section.id] || ''}
                            onChange={e => setNewAction(p => ({ ...p, [section.id]: e.target.value }))}
                            onKeyDown={e => e.key === 'Enter' && addAction(section.id)}
                          />
                          <button className="adm-add-btn" onClick={() => addAction(section.id)}>Add action</button>
                        </div>
                      </div>
                      )}
                      {confirmDelete === section.id ? (
                        <div className="adm-confirm">
                          <div className="adm-confirm-text">Delete this section? This will remove the section, its log items and corrective actions.</div>
                          <div className="adm-confirm-btns">
                            <button className="adm-confirm-cancel" onClick={() => setConfirmDelete(null)}>Cancel</button>
                            <button className="adm-confirm-delete" onClick={() => { removeSection(section.id); setConfirmDelete(null); }}>Delete</button>
                          </div>
                        </div>
                      ) : (
                        <button className="adm-delete-section-btn" onClick={() => setConfirmDelete(section.id)}>Delete section</button>
                      )}
                    </div>
                  )}
                </div>
              );

              const renderTypeRow = (key, val) => {
                const rule = val.threshold.max !== undefined ? `Max ${val.threshold.max}°C`
                  : val.threshold.min !== undefined ? `Min ${val.threshold.min}°C` : 'Custom';
                const on = newSectionType === key;
                return (
                  <button key={key} className={`adm-new-type-row${on ? ' adm-new-type-row--on' : ''}`} onClick={() => setNewSectionType(key)}>
                    <div>
                      <div className="adm-new-type-row-name">{val.label}</div>
                      <div className="adm-new-type-row-rule">{rule}</div>
                    </div>
                    {on && <div className="adm-new-type-check"><svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg></div>}
                  </button>
                );
              };

              const addSectionForm = (groupId = null) => addingSection === (groupId ?? true) ? (
                <div className="adm-card">
                  <div className="adm-card-hdr" style={{ cursor: 'default' }}>
                    <span className="adm-card-title">New section</span>
                  </div>
                  <div className="adm-card-body">
                    <div className="adm-group-label">Type</div>
                    <div className="adm-new-type-list">
                      {['cold','freezer','hot','reheating','delivery','custom'].map(key => renderTypeRow(key, SECTION_TYPES[key]))}
                    </div>
                    <div className="adm-group-label" style={{ marginTop: 4 }}>Name <span className="adm-optional">(optional)</span></div>
                    <input className="adm-add-input" placeholder={SECTION_TYPES[newSectionType].label}
                      value={newSectionName} onChange={e => setNewSectionName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addSection(groupId)}
                    />
                    <div className="adm-confirm-btns" style={{ marginTop: 4 }}>
                      <button className="adm-confirm-cancel" onClick={() => { setAddingSection(false); setNewSectionName(''); setNewSectionType('cold'); }}>Cancel</button>
                      <button className="adm-add-btn" onClick={() => addSection(groupId)}>Add section</button>
                    </div>
                  </div>
                </div>
              ) : (
                <button className="adm-add-section-btn" onClick={() => { setAddingToGroup(groupId); setAddingSection(groupId ?? true); }}>+ Add section</button>
              );

              if (isGrouped(sections)) {
                return (
                  <>
                    {(sections.groups || []).map(group => (
                      <div key={group.id} className="adm-dept-group">
                        <div className="adm-dept-hdr">
                          <input
                            className="adm-dept-name-input"
                            value={group.name}
                            onChange={e => updateGroupName(group.id, e.target.value)}
                          />
                          {confirmDeleteGroup === group.id ? (
                            <div className="adm-confirm" style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                              <span className="adm-confirm-text" style={{ fontSize: 13 }}>Delete department?</span>
                              <button className="adm-confirm-cancel" onClick={() => setConfirmDeleteGroup(null)}>No</button>
                              <button className="adm-confirm-delete" onClick={() => { removeGroup(group.id); setConfirmDeleteGroup(null); }}>Yes</button>
                            </div>
                          ) : (
                            <button className="adm-dept-delete-btn" onClick={() => setConfirmDeleteGroup(group.id)}>Delete dept</button>
                          )}
                        </div>
                        {(group.sections || []).map(renderSectionCard)}
                        {addSectionForm(group.id)}
                      </div>
                    ))}
                    <div className="adm-section-actions">
                      <button className="adm-add-section-btn" onClick={addGroup}>+ Add department</button>
                      {venue?.id && (
                        <button className="adm-wizard-btn adm-wizard-btn--reset" onClick={() => setConfirmReset(true)}>Reset sections</button>
                      )}
                    </div>
                  </>
                );
              }

              const isTemp = s => Object.keys(s.threshold || {}).length > 0;
              const tempSecs = resolvedSections.filter(isTemp);
              const taskSecs = resolvedSections.filter(s => !isTemp(s));
              const STG_GROUPS = [
                { key: 'fridges',  label: 'Fridges',     singularLabel: 'Fridge',           type: 'cold',      thresholdKey: 'max' },
                { key: 'freezers', label: 'Freezers',    singularLabel: 'Freezer',          type: 'freezer',   thresholdKey: 'max' },
                { key: 'hot',      label: 'Hot Holding', singularLabel: 'Hot Holding unit', type: 'hot',       thresholdKey: 'min' },
                { key: 'cooking',  label: 'Cooking',     singularLabel: 'Cooking section',  type: 'reheating', thresholdKey: 'min' },
              ];
              return (
                <>
                  <div className="stg-tiles-grid">
                    {STG_GROUPS.map(g => {
                      const count = tempSecs.filter(s => getEquipmentGroup(s) === g.key).length;
                      const isOpen = openSettingsGroup === g.key;
                      return (
                        <button key={g.key}
                          className={`sec-tile${isOpen ? ' sec-tile--done' : ''}`}
                          onClick={() => setOpenSettingsGroup(isOpen ? null : g.key)}>
                          <div className="sec-tile-name">{g.label}</div>
                          <div className={`sec-tile-badge${isOpen ? ' sec-tile-badge--done' : ' sec-tile-badge--pending'}`}>
                            {isOpen ? 'Editing' : count > 0 ? `${count} unit${count !== 1 ? 's' : ''}` : 'Tap to set up'}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {openSettingsGroup && (() => {
                    const g = STG_GROUPS.find(g => g.key === openSettingsGroup);
                    if (!g) return null;
                    const groupSecs = tempSecs.filter(s => getEquipmentGroup(s) === g.key);
                    return (
                      <div className="stg-group-card">
                        <div className="stg-group-card-hdr">{g.label}</div>
                        {groupSecs.length === 0 && (
                          <div className="stg-group-empty">No {g.label.toLowerCase()} added yet</div>
                        )}
                        {groupSecs.map(section => (
                          <div key={section.id} className="stg-unit-row">
                            <input className="adm-add-input"
                              value={section.title}
                              onChange={e => updateSection(section.id, 'title', e.target.value)} />
                            <div className="stg-unit-fields">
                              <div className="stg-unit-field">
                                <span className="adm-threshold-lbl">{g.thresholdKey === 'max' ? 'Max temp (°C)' : 'Min temp (°C)'}</span>
                                <input className="stg-temp-input" type="number"
                                  value={section.threshold[g.thresholdKey] ?? ''}
                                  onChange={e => updateThreshold(section.id, g.thresholdKey, e.target.value)} />
                              </div>
                              <div className="stg-unit-field">
                                <span className="adm-threshold-lbl">Buffer (°C)</span>
                                <input className="stg-temp-input" type="number" min="0"
                                  placeholder={`${getDefaultBuffer(section)}`}
                                  value={section.buffer ?? ''}
                                  onChange={e => updateSection(section.id, 'buffer', e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                              </div>
                            </div>
                            {confirmDelete === section.id ? (
                              <div className="stg-unit-confirm">
                                <button className="adm-confirm-cancel" onClick={() => setConfirmDelete(null)}>No</button>
                                <button className="adm-confirm-delete" onClick={() => { removeSection(section.id); setConfirmDelete(null); }}>Delete</button>
                              </div>
                            ) : (
                              <button className="stg-unit-del" onClick={() => setConfirmDelete(section.id)}>Remove</button>
                            )}
                          </div>
                        ))}
                        <button className="adm-add-section-btn" onClick={() => addSectionToGroup(g.key)}>
                          + Add {g.singularLabel}
                        </button>
                      </div>
                    );
                  })()}

                  <div className="adm-labelled-group">
                    <div className="adm-labelled-group-hdr">Checklists</div>
                    {taskSecs.map(renderSectionCard)}
                    {addingSection === 'task' ? (
                      <div className="adm-card">
                        <div className="adm-card-body">
                          <div className="adm-group-label">Name</div>
                          <input className="adm-add-input" placeholder="e.g. Opening Checks"
                            value={newSectionName} onChange={e => setNewSectionName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && addSection()}
                          />
                          <div className="adm-confirm-btns" style={{ marginTop: 12 }}>
                            <button className="adm-confirm-cancel" onClick={() => { setAddingSection(false); setNewSectionName(''); setNewSectionType('cold'); }}>Cancel</button>
                            <button className="adm-add-btn" onClick={() => addSection()}>Add checklist</button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button className="adm-add-section-btn" onClick={() => { setNewSectionType('custom'); setAddingSection('task'); }}>+ Add checklist</button>
                    )}
                  </div>

                  {venue?.id && (
                    <div className="adm-section-actions">
                      <button className="adm-wizard-btn adm-wizard-btn--reset" onClick={() => setConfirmReset(true)}>Reset sections</button>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {settingsTab === 'alerts' && (
          <div className="adm-tab-content">
            <div className="adm-notif-section">
              <div className="adm-notif-heading">Push notifications</div>
              <p className="adm-notif-sub">Alerts go to all admins for this venue.</p>
              <div className="adm-card">
                {[
                  { key: 'failedTemp', label: 'Failed temperature alerts', sub: 'Instant alert when a temp log fails.' },
                  { key: 'correctiveAction', label: 'Corrective action logged', sub: 'Alert when staff log a corrective action.' },
                  { key: 'allComplete', label: 'All checks complete', sub: 'Alert when all daily checks are done.' },
                ].map(({ key, label, sub }, i, arr) => (
                  <div key={key} className={`adm-notif-row${i < arr.length - 1 ? ' adm-notif-row--bordered' : ''}`}>
                    <div className="adm-notif-row-text">
                      <div className="adm-notif-row-label">{label}</div>
                      <div className="adm-notif-row-sub">{sub}</div>
                    </div>
                    <button
                      className={`adm-notif-toggle${notifPrefs[key] ? ' adm-notif-toggle--on' : ''}`}
                      onClick={() => {
                        const next = { ...notifPrefs, [key]: !notifPrefs[key] };
                        setNotifPrefs(next);
                        if (venue?.id) {
                          supabase.from('venues').update({ notification_prefs: next }).eq('id', venue.id)
                            .then(({ error }) => { if (!error) showToast('Saved', 'success'); else { setNotifPrefs(notifPrefs); showToast('Save failed', 'error'); } });
                        }
                      }}
                    >
                      <div className="adm-notif-toggle-thumb" />
                    </button>
                  </div>
                ))}
              </div>
              <button className="adm-notif-device-btn" onClick={() => setShowNotifSheet(true)}>
                {typeof Notification !== 'undefined' && Notification.permission === 'granted'
                  ? '✓ Notifications enabled on this device'
                  : 'Enable notifications on this device'}
              </button>
            </div>

            <div className="adm-settings-section">
              <div className="adm-settings-section-title">Check schedule</div>
              <div className="adm-notif-card">
                <div className="adm-notif-row">
                  <div className="adm-notif-row-text">
                    <div className="adm-notif-row-label">Checks per day</div>
                    <div className="adm-notif-row-sub">How many times staff log temperatures daily.</div>
                  </div>
                </div>
                <div className="shift-freq-options">
                  {[
                    { id: '1x', label: '1×', sub: 'Once daily' },
                    { id: '2x', label: '2×', sub: 'AM & PM' },
                    { id: '3x', label: '3×', sub: 'Morn / Mid / Eve' },
                    { id: '4x', label: '4×', sub: 'Brekky–Dinner' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      className={`shift-freq-opt${(venue?.check_frequency || '2x') === opt.id ? ' shift-freq-opt--active' : ''}`}
                      onClick={() => {
                        if (!venue?.id) return;
                        supabase.from('venues').update({ check_frequency: opt.id }).eq('id', venue.id)
                          .then(({ error }) => {
                            if (!error) { onVenueUpdate({ check_frequency: opt.id }); showToast('Saved', 'success'); }
                            else showToast('Save failed', 'error');
                          });
                      }}
                    >
                      <span className="shift-freq-opt-label">{opt.label}</span>
                      <span className="shift-freq-opt-sub">{opt.sub}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {settingsTab === 'cleaning' && (
          <div className="adm-tab-content">
            <div className="adm-cleaning-header">
              <p className="adm-notif-sub">Add tasks that staff need to complete on a schedule. They appear in the Checklists tab when due.</p>
              <button className="adm-cleaning-add-btn" onClick={() => { setEditingTask('new'); setTaskDraft({ frequency: 'daily' }); }}>+ Add task</button>
            </div>

            {editingTask && (
              <div className="adm-cleaning-form">
                <input
                  className="adm-cleaning-input"
                  placeholder="Task name"
                  value={taskDraft.name || ''}
                  onChange={e => setTaskDraft(p => ({ ...p, name: e.target.value }))}
                />
                <input
                  className="adm-cleaning-input"
                  placeholder="Area (e.g. Kitchen, Bar)"
                  value={taskDraft.area || ''}
                  onChange={e => setTaskDraft(p => ({ ...p, area: e.target.value }))}
                />
                <select
                  className="adm-cleaning-select"
                  value={taskDraft.frequency || 'daily'}
                  onChange={e => setTaskDraft(p => ({ ...p, frequency: e.target.value, specific_days: [] }))}
                >
                  <option value="daily">Daily</option>
                  <option value="specific_days">Specific days</option>
                  <option value="weekly">Weekly</option>
                  <option value="fortnightly">Fortnightly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="adhoc">As needed</option>
                </select>
                {taskDraft.frequency === 'specific_days' && (
                  <div className="adm-cleaning-days">
                    {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d, i) => (
                      <button
                        key={d}
                        className={`adm-cleaning-day${(taskDraft.specific_days || []).includes(i) ? ' adm-cleaning-day--on' : ''}`}
                        onClick={() => setTaskDraft(p => {
                          const days = p.specific_days || [];
                          return { ...p, specific_days: days.includes(i) ? days.filter(x => x !== i) : [...days, i] };
                        })}
                      >{d}</button>
                    ))}
                  </div>
                )}
                <textarea
                  className="adm-cleaning-input adm-cleaning-textarea"
                  placeholder="Instructions (optional)"
                  value={taskDraft.instructions || ''}
                  onChange={e => setTaskDraft(p => ({ ...p, instructions: e.target.value }))}
                  rows={2}
                />
                <div className="adm-cleaning-form-btns">
                  <button className="adm-cleaning-cancel" onClick={() => { setEditingTask(null); setTaskDraft({}); }}>Cancel</button>
                  <button className="adm-cleaning-save" disabled={!taskDraft.name?.trim() || savingTask} onClick={saveCleaningTask}>
                    {savingTask ? 'Saving…' : 'Save task'}
                  </button>
                </div>
              </div>
            )}

            {cleaningTasksLoaded && cleaningTasks.length === 0 && !editingTask && (
              <div className="adm-cleaning-empty">No cleaning tasks yet. Add one above.</div>
            )}

            {cleaningTasks.map(task => (
              <div key={task.id} className="adm-cleaning-task">
                <div className="adm-cleaning-task-left">
                  <span className="adm-cleaning-task-name">{task.name}</span>
                  <span className="adm-cleaning-task-meta">
                    {task.area ? `${task.area} · ` : ''}
                    {task.frequency === 'specific_days'
                      ? ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].filter((_, i) => (task.specific_days || []).includes(i)).join(', ')
                      : { daily: 'Daily', weekly: 'Weekly', fortnightly: 'Fortnightly', monthly: 'Monthly', quarterly: 'Quarterly', adhoc: 'As needed' }[task.frequency]}
                  </span>
                </div>
                <div className="adm-cleaning-task-actions">
                  <button className="adm-cleaning-task-edit" onClick={() => { setEditingTask(task); setTaskDraft({ ...task }); }}>Edit</button>
                  <button className="adm-cleaning-task-delete" onClick={() => deleteCleaningTask(task.id)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {settingsTab === 'team' && (
          <div className="adm-tab-content">
            {venue?.id && (() => {
              const inviteUrl = `${window.location.origin}${window.location.pathname}?invite=${venue.id}&role=${inviteRole}`;
              const handleSendInvite = async () => {
                if (!inviteEmail.trim()) return;
                try {
                  await supabase.from('invites').insert({
                    venue_id: venue.id,
                    email: inviteEmail.trim(),
                    name: inviteName.trim() || null,
                    role: inviteRole,
                  });
                  await supabase.functions.invoke('invite-staff', {
                    body: { to_email: inviteEmail.trim(), to_name: inviteName.trim(), venue_name: venue.name, invite_url: inviteUrl },
                  });
                  showToast(`Invite sent to ${inviteEmail.trim()}`, 'success');
                  refreshTeam();
                } catch {
                  showToast('Could not send invite. Try again.', 'error');
                }
                setInviteName('');
                setInviteEmail('');
                setInviteRole('staff');
              };
              return (
                <div className="adm-invite-box">
                  <div className="adm-invite-row">
                    <input className="adm-invite-input" placeholder="Name" value={inviteName} onChange={e => setInviteName(e.target.value)} />
                    <input className="adm-invite-input" type="email" placeholder="Email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} />
                  </div>
                  <div className="adm-invite-footer">
                    <div className="adm-role-toggle">
                      <button className={`adm-role-opt${inviteRole === 'staff' ? ' adm-role-opt--on' : ''}`} onClick={() => setInviteRole('staff')}>Staff</button>
                      <button className={`adm-role-opt${inviteRole === 'admin' ? ' adm-role-opt--on' : ''}`} onClick={() => setInviteRole('admin')}>Admin</button>
                    </div>
                    <button className="adm-invite-copy" onClick={handleSendInvite} disabled={!inviteEmail.trim()}>Send invite</button>
                  </div>
                </div>
              );
            })()}
            {team.map(member => (
              <div key={member.id} className="adm-card adm-card--flat">
                <div className="adm-member-info">
                  <div className="adm-member-name-row">
                    <span className="adm-member-name">{member.name}</span>
                    <span className={`adm-role-badge ${member.role === 'admin' ? 'adm-role-badge--admin' : ''}`}>
                      {member.role === 'admin' ? 'Admin' : 'Staff'}
                    </span>
                  </div>
                  {member.email && <div className="adm-member-email">{member.email}</div>}
                </div>
                <div className="adm-member-actions">
                  {confirmRemove === member.id ? (
                    <div className="adm-member-confirm">
                      <span className="adm-confirm-label">Remove?</span>
                      <button className="adm-confirm-cancel" onClick={() => setConfirmRemove(null)}>No</button>
                      <button className="adm-confirm-delete" onClick={() => removeMember(member.id)}>Yes</button>
                    </div>
                  ) : (
                    <button className="adm-remove-text-btn" onClick={() => setConfirmRemove(member.id)}>Remove</button>
                  )}
                </div>
              </div>
            ))}
            {pendingInvites.map(invite => (
              <div key={invite.id} className={`adm-card adm-card--flat adm-invite-card${invite.accepted_at ? ' adm-invite-card--accepted' : ''}`}>
                <div className="adm-member-info">
                  <div className="adm-member-name-row">
                    <span className="adm-member-name">{invite.name || invite.email}</span>
                    <span className={`adm-role-badge ${invite.role === 'admin' ? 'adm-role-badge--admin' : ''}`}>
                      {invite.role === 'admin' ? 'Admin' : 'Staff'}
                    </span>
                  </div>
                  <div className="adm-member-email">{invite.email}</div>
                </div>
                <div className="adm-invite-status">
                  {invite.accepted_at
                    ? <span className="adm-invite-badge adm-invite-badge--accepted">Accepted</span>
                    : <span className="adm-invite-badge adm-invite-badge--pending">Invite sent</span>
                  }
                </div>
              </div>
            ))}
          </div>
        )}


        <div className="adm-subscription-card">
          <div className="adm-subscription-card-left">
            <div className="adm-subscription-card-title">Subscription</div>
            <div className="adm-subscription-card-trial">
              {trialMsLeft !== null
                ? trialMsLeft === 0
                  ? 'Trial expired.'
                  : trialDays > 0
                    ? `${trialDays}d ${trialHours}h left on your trial.`
                    : `${trialHours}h left on your trial.`
                : 'Manage your subscription below.'}
            </div>
          </div>
          <button className="adm-subscription-btn" disabled>Coming soon</button>
        </div>

      </div>
      )}
      </div>
      <BottomNav tabs={ADMIN_TABS} active={view} onChange={(v) => v === 'settings' ? goToSettings() : setView(v)} />
      {showOnboarding && (
        <OnboardingModal onComplete={handleOnboardingComplete} />
      )}
      {confirmReset && (
        <div className="modal-overlay" onClick={() => setConfirmReset(false)}>
          <div className="modal-sheet modal-sheet--confirm" onClick={e => e.stopPropagation()}>
            <div className="modal-confirm-icon">⚠️</div>
            <h3 className="modal-confirm-title">Reset all sections?</h3>
            <p className="modal-confirm-body">This will permanently delete all your sections and checklist items. Your logged data will be kept, but the app will go back to the setup wizard. This cannot be undone.</p>
            <div className="modal-confirm-actions">
              <button className="modal-confirm-cancel" onClick={() => setConfirmReset(false)}>Cancel</button>
              <button className="modal-confirm-delete" onClick={() => {
                setConfirmReset(false);
                if (venue?.id) {
                  localStorage.removeItem(`feja_ob_${venue.id}`);
                  supabase.from('venues').update({ sections: null }).eq('id', venue.id);
                }
                setSections(null);
                setShowOnboarding(true);
              }}>Reset sections</button>
            </div>
          </div>
        </div>
      )}
      {showNotifSheet && (
        <NotificationsSheet user={user} venue={venue} onDone={() => {
          setShowNotifSheet(false);
          const isInstalled = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
          const dismissed = localStorage.getItem('feja_install_dismissed');
          if (!isInstalled && !dismissed) setTimeout(() => setShowInstallSheet(true), 400);
        }} />
      )}
      {showInstallSheet && (
        <InstallSheet onDone={() => setShowInstallSheet(false)} />
      )}
    </div>
  );
}

const WELCOME_PHRASES = [
  { text: 'Welcome back',          pronunciation: 'wel-kum bak' },
  { text: 'Bienvenido de nuevo',   pronunciation: 'byen-veh-NEE-doh deh NWEH-voh' },
  { text: 'Benvenuto',             pronunciation: 'ben-veh-NOO-toh' },
  { text: 'Bienvenue',             pronunciation: 'byeh-veh-NOO' },
  { text: 'Willkommen',            pronunciation: 'vil-KOM-en' },
  { text: 'Bem-vindo',             pronunciation: 'beng-VEEN-doo' },
  { text: 'Καλώς ήρθες',           pronunciation: 'kah-LOS EER-thes' },
  { text: 'Welkom',                pronunciation: 'VEL-kom' },
  { text: 'Добро пожаловать',      pronunciation: 'dob-ROH pah-ZHAH-luh-vat' },
  { text: '欢迎回来',               pronunciation: 'hwahn-ying hway-lye' },
];


function App() {
  const [screen, setScreen] = useState(null);
  const [user, setUser] = useState(null);
  const [venue, setVenue] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [welcomeIdx, setWelcomeIdx] = useState(0);
  const [welcomeFade, setWelcomeFade] = useState(true);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const isRecovery = useRef(false);
  const [swReg, setSwReg] = useState(null);

  useEffect(() => {
    const handler = (e) => setSwReg(e.detail);
    window.addEventListener('swUpdateReady', handler);
    return () => window.removeEventListener('swUpdateReady', handler);
  }, []);
  const showToast = useCallback((msg, type = 'default') => {
    clearTimeout(toastTimer.current);
    setToast({ msg, type, key: Date.now() });
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }, []);

  const loadUserData = async (authUser) => {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*, venues(*)')
      .eq('id', authUser.id)
      .single();

    if (error || !profile) {
      console.error('loadUserData: no profile found', error);
      setAuthError('Account found but profile could not be loaded. Please contact support.');
      setScreen('login');
      return;
    }

    if (profile.venues) setVenue(profile.venues);
    setScreen(profile.role === 'admin' ? 'admin' : 'staff');
  };

  useEffect(() => {
    const pathname = window.location.pathname;
    if (pathname === '/preview') {
      setScreen('landing-preview');
      return;
    }
    if (pathname === '/terms') {
      setScreen('legal-terms');
      return;
    }
    if (pathname === '/privacy') {
      setScreen('legal-privacy');
      return;
    }
    if (pathname === '/refunds') {
      setScreen('legal-refund');
      return;
    }
    if (pathname !== '/' && pathname !== '' && pathname !== '/landing') {
      setScreen('notfound');
      return;
    }

    const loginDirect = new URLSearchParams(window.location.search).get('login') === 'true';
    const isAppDomain = window.location.hostname === 'app.feja.com.au' || window.location.hostname === 'feja-app.vercel.app';

    // Detect password recovery before getSession resolves to prevent flash
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    if (hashParams.get('type') === 'recovery') {
      isRecovery.current = true;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (isRecovery.current) return;
      if (session?.user) {
        setUser(session.user);
        loadUserData(session.user);
      } else {
        const inviteParam = new URLSearchParams(window.location.search).get('invite');
        if (inviteParam) setScreen('linktree');
        else if (loginDirect || isAppDomain) setScreen('login');
        else setScreen('home');
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (_event === 'PASSWORD_RECOVERY') {
        isRecovery.current = true;
        setScreen('reset');
        return;
      }
      if (_event === 'SIGNED_IN' && session?.user) {
        if (isRecovery.current) return;
        setUser(session.user);
        loadUserData(session.user);
        return;
      }
      if (!session) {
        setUser(null);
        setVenue(null);
        setScreen(isAppDomain ? 'login' : 'home');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setWelcomeFade(false);
      setTimeout(() => {
        setWelcomeIdx(i => (i + 1) % WELCOME_PHRASES.length);
        setWelcomeFade(true);
      }, 400);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const handleLogin = async () => {
    setLoginLoading(true);
    setAuthError('');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    setLoginLoading(false);
    if (error) {
      setAuthError('Invalid email or password');
      return;
    }
    setUser(data.user);
    await loadUserData(data.user);
  };

  const handleForgotPassword = async () => {
    setForgotLoading(true);
    await supabase.auth.resetPasswordForEmail(forgotEmail, { redirectTo: window.location.origin });
    setForgotLoading(false);
    setForgotSent(true);
  };

  const handleResetPassword = async () => {
    setResetLoading(true);
    setResetError('');
    const { error } = await supabase.auth.updateUser({ password: resetPassword });
    setResetLoading(false);
    if (error) { setResetError(error.message || 'Could not update password. Please try again.'); return; }
    isRecovery.current = false;
    await supabase.auth.signOut();
    setResetPassword('');
    setScreen('login');
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setVenue(null);
    setScreen('login');
  };


  if (screen === null) {
    return (
      <>
        <LazyBackground />
        <div className="feja-app">
          <div className="screen lt">
            <div className="lt-body">
              <img src={logo} alt="Feja" className="lt-wordmark" />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (screen === 'notfound') return <NotFound404 />;
  if (screen === 'legal-terms') return <LegalModal doc="terms" onClose={() => window.history.back()} />;
  if (screen === 'legal-privacy') return <LegalModal doc="privacy" onClose={() => window.history.back()} />;
  if (screen === 'legal-refund') return <LegalModal doc="refund" onClose={() => window.history.back()} />;

  if (screen === 'linktree') {
    const inviteVenueId = new URLSearchParams(window.location.search).get('invite');
    return (
      <>
        <LazyBackground />
        <LinktreePage
          inviteVenueId={inviteVenueId}
          startOnForm={!inviteVenueId}
          onOpenApp={() => setScreen('login')}
          onSignedUp={(newUser) => { setUser(newUser); loadUserData(newUser); }}
        />
      </>
    );
  }

  if (screen === 'home') {
    const onMarketingSite = window.location.hostname === 'feja.com.au' || window.location.hostname === 'www.feja.com.au';
    return <LandingPage onSignIn={onMarketingSite ? () => { window.location.href = 'https://app.feja.com.au?login=true'; } : () => setScreen('login')} />;
  }

  return (
    <>
    {screen === 'login' && <LazyBackground />}
    <div className="feja-app">
      {screen === 'login' && (
        <div className="screen login-page">
          <button className="login-back-btn" aria-label="Home" onClick={() => {
            if (window.location.hostname === 'app.feja.com.au') window.location.href = 'https://feja.com.au';
            else setScreen('home');
          }}>←</button>
          <div className="login-top">
            <div className="login-logo-sm">
              <img src={logo} alt="Feja" />
            </div>
            <div className={`welcome-wrap ${welcomeFade ? 'welcome-in' : 'welcome-out'}`}>
              <h1 className="login-title">{WELCOME_PHRASES[welcomeIdx].text}</h1>
              <p className="welcome-pronunciation">{WELCOME_PHRASES[welcomeIdx].pronunciation}</p>
            </div>
            <p className="login-sub">Sign in to your kitchen</p>
          </div>
          <div className="login-form">
            <label className="f-lbl">Email</label>
            <input
              className="f-input"
              type="email"
              placeholder="you@venue.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <label className="f-lbl">Password</label>
            <div className="pw-wrap">
              <input
                className="f-input"
                type={showLoginPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
              />
              <button type="button" className="pw-toggle" aria-label={showLoginPassword ? 'Hide password' : 'Show password'} onClick={() => setShowLoginPassword(p => !p)}>
                {showLoginPassword
                  ? <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
              </button>
            </div>
            {authError && <p className="auth-error">{authError}</p>}
            <p className="forgot" onClick={() => { setForgotEmail(email); setForgotSent(false); setScreen('forgot'); }}>Forgot password?</p>
            <button className="btn-primary" onClick={handleLogin} disabled={loginLoading}>
              {loginLoading ? 'Signing in...' : 'Sign in'}
            </button>
            <p className="forgot" style={{textAlign:'center',marginTop:'16px'}} onClick={() => setScreen('linktree')}>No account? Start free trial</p>
          </div>
        </div>
      )}

      {screen === 'forgot' && (
        <div className="screen login-page">
          <button className="login-back-btn" onClick={() => setScreen('login')}>←</button>
          <div className="login-top">
            <div className="login-logo-sm"><img src={logo} alt="Feja" /></div>
            <h1 className="login-title">Reset password</h1>
            <p className="login-sub">We'll send a reset link to your email</p>
          </div>
          <div className="login-form">
            {forgotSent ? (
              <div className="forgot-sent">
                <p>Check your inbox, a reset link is on its way to <strong>{forgotEmail}</strong>.</p>
                <button className="btn-primary" style={{marginTop: '20px'}} onClick={() => setScreen('login')}>Back to sign in</button>
              </div>
            ) : (
              <>
                <label className="f-lbl">Email</label>
                <input
                  className="f-input"
                  type="email"
                  placeholder="you@venue.com"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleForgotPassword()}
                />
                <button className="btn-primary" onClick={handleForgotPassword} disabled={forgotLoading || !forgotEmail}>
                  {forgotLoading ? 'Sending...' : 'Send reset link'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {screen === 'reset' && (
        <div className="screen login-page">
          <div className="login-top">
            <div className="login-logo-sm"><img src={logo} alt="Feja" /></div>
            <h1 className="login-title">New password</h1>
            <p className="login-sub">Choose something strong</p>
          </div>
          <div className="login-form">
            <label className="f-lbl">New password</label>
            <div className="pw-wrap">
              <input
                className="f-input"
                type={showResetPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={resetPassword}
                onChange={e => setResetPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleResetPassword()}
              />
              <button type="button" className="pw-toggle" aria-label={showResetPassword ? 'Hide password' : 'Show password'} onClick={() => setShowResetPassword(p => !p)}>
                {showResetPassword
                  ? <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
              </button>
            </div>
            {resetError && <p className="auth-error">{resetError}</p>}
            <button className="btn-primary" onClick={handleResetPassword} disabled={resetLoading || resetPassword.length < 6}>
              {resetLoading ? 'Updating...' : 'Set new password'}
            </button>
          </div>
        </div>
      )}

      {screen === 'staff' && (
        <StaffApp onSignOut={handleSignOut} user={user} venue={venue} />
      )}

      {screen === 'admin' && (
        <AdminDashboard onSignOut={handleSignOut} user={user} venue={venue} onVenueUpdate={updates => setVenue(v => ({ ...v, ...updates }))} showToast={showToast} />
      )}

      {(screen === 'admin' || screen === 'staff') && <InstallBanner />}
      {toast && <Toast key={toast.key} msg={toast.msg} type={toast.type} />}
      {swReg && (
        <div className="sw-update-banner">
          <span className="sw-update-text">Update available</span>
          <button className="sw-update-btn" onClick={() => {
            swReg.waiting?.postMessage({ type: 'SKIP_WAITING' });
            navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload());
          }}>Refresh</button>
        </div>
      )}

      {screen === 'preview' && (
        <PreviewWrapper onExit={() => { setUser(null); setScreen('login'); }} />
      )}

      {screen === 'landing-preview' && <LandingPage />}
    </div>
    </>
  );
}

export default App;
